import React, { useState, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../AuthContext';
import { QUOTE_BY_ID_QUERY } from '../graphql/quotes';
import { ALL_USERS_QUERY, CREATE_WORK_ORDER_MUTATION, CREATE_WORK_ORDER_LINE_ITEM_MUTATION, CREATE_WORK_ORDER_IMAGE_MUTATION } from '../graphql/workOrders';
import PhotoGallery, { GalleryPhoto } from '../components/PhotoGallery';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const SERVER_URL = process.env.REACT_APP_API_HOST || 'http://localhost:8080';

interface CreateWorkOrderProps {
  quoteId: number;
  onClose: () => void;
  onComplete?: (workOrderId: number) => void;
}

// Every work order type now sources exclusively from draft_quote_object rows
// (quote.draftQuoteByDraftQuoteId.draftQuoteObjectsByDraftQuoteId) — not from
// quote_line_item. Quote-line-item-based sourcing was ambiguous: finalize-time
// combining (combineByDescriptionAndPrice in finalizeQuotePlugin.ts) can merge
// several objects' pricing rows into one quote_line_item when they match on
// description+price, so that FK didn't reliably trace back to a single source
// object. Reading the object's own (never-merged) draft_quote_object_item rows
// sidesteps that entirely, and it's what lets a single drag pull in every field
// a type needs (fabric rows, Rod Face/Hem/Returns/Overlaps, board specs, etc.)
// without re-typing anything by hand.
interface DraftQuoteObject {
  id: number;
  name: string | null;
  itemType: string | null;
  quantity: number;
  widthInches: number | null;
  heightInches: number | null;
  depthInches: number | null;
  fabricDescription: string | null;
  liningType: string | null;
  bumpInterlining: boolean;
  interliningType: string | null;
  trimPlacement: string | null;
  trimDescription: string | null;
  operatingSystem: string | null;
  hardwareType: string | null;
  mountType: string | null;
  rollDirection: string | null;
  driveSide: string | null;
  chainColor: string | null;
  rodFaceInches: number | null;
  overlapInches: number | null;
  returnInches: number | null;
  hemSize: string | null;
  pleatType: string | null;
  spaceAboveWindowInches: number | null;
  flapSizeInches: number | null;
  weltType: string | null;
  contrastWeltFabricDescription: string | null;
  insertType: string | null;
  ruffle: boolean;
  shamSize: string | null;
  bedSize: string | null;
  dustskirtStyle: string | null;
  dropInches: number | null;
  fabricWidthInches: number | null;
  patternRepeatInches: number | null;
  duvetInsert: boolean | null;
  seatHeightInches: number | null;
  seatDepthInches: number | null;
  armHeightInches: number | null;
  backHeightInches: number | null;
  legHeightInches: number | null;
  cushionThicknessInches: number | null;
  armThicknessInches: number | null;
  seatDeckThicknessInches: number | null;
  backCushionThicknessInches: number | null;
  legProfile: string | null;
  furnitureType: string | null;
  benchType: string | null;
  benchShape: string | null;
  benchStyle: string | null;
  storageOption: boolean | null;
  channelSize: string | null;
  pillowTopSize: string | null;
  legSize: string | null;
  legStyle: string | null;
  legFinish: string | null;
  legPriceCents: number | null;
  trimTop: boolean | null;
  trimBottom: boolean | null;
  nailheadTop: boolean | null;
  nailheadBottom: boolean | null;
  nailheadColor: string | null;
  corniceShape: string | null;
  corniceOverProduct: boolean | null;
  nailheadSides: boolean | null;
  nailheadFace: boolean | null;
  weltTop: boolean | null;
  weltBottom: boolean | null;
  weltSides: boolean | null;
  weltFace: boolean | null;
  bedConfig: string | null;
  headboardStyle: string | null;
  headboardShaped: boolean | null;
  bedEdgeTreatment: string | null;
  trimSides: boolean | null;
  trimFace: boolean | null;
  mattressLengthInches: number | null;
  mattressWidthInches: number | null;
  frameThicknessInches: number | null;
  sideRailFootboardHeightInches: number | null;
  sideRailFootboardStyleDescription: string | null;
  bedDrawersSmallQty: number | null;
  bedDrawersLargeQty: number | null;
  bedTrundle: boolean | null;
  bedFeetType: string | null;
  bedFeetHeightInches: number | null;
  bedFeetFinish: string | null;
  bedFeetSpecialtyDescription: string | null;
  bedFeetSpecialtyPriceCents: number | null;
  bedBunkyBoards: boolean | null;
  bedBunkyBoardWidthInches: number | null;
  includeMeasureCharge: boolean | null;
  notes: string | null;
  draftQuoteObjectItemsByDraftQuoteObjectId?: {
    nodes: { id: number; section: string; description: string; quantity: number; unitPrice: number; archivedAt: string | null }[];
  };
}

// ── Spec label maps ──────────────────────────────────────────────────────────

const LINING_LABELS: Record<string, string> = {
  standard: 'Standard', none: 'None', blackout: 'Blackout', sheer: 'Sheer', interlining: 'Interlining',
};

const INTERLINING_LABELS: Record<string, string> = {
  none: 'None', bump: 'Bump', flannel: 'Flannel',
};

const HARDWARE_LABELS: Record<string, string> = {
  hospital_track: 'Hospital Track', rod_and_rings: 'Rod & Rings', ctm: 'CTM', traversing_rod: 'Traversing Rod',
};

const OS_LABELS: Record<string, string> = {
  manual: 'Manual', somfy_standard: 'Somfy', somfy: 'Somfy',
  rollease: 'Rollease', cord_cleat: 'Cord & Cleat',
};

const PLEAT_LABELS: Record<string, string> = {
  top_pinch: 'Top Pinch', traditional: 'Traditional', inverted: 'Inverted',
  cartridge: 'Cartridge', butterfly: 'Butterfly', ripplefold: 'Ripplefold',
};

const WELT_LABELS: Record<string, string> = { self: 'Self Welt', contrast: 'Contrast Welt' };
const INSERT_LABELS: Record<string, string> = { '90_10': '90/10', '50_50': '50/50', poly: 'Poly', foam: 'Foam', down: 'Down', dacron_foam: 'Dacron Wrapped Foam', down_wrapped: 'Down Wrapped Foam' };
const SHAM_SIZE_LABELS: Record<string, string> = { standard: 'Standard', euro: 'Euro', queen: 'Queen', king: 'King' };
const BED_SIZE_LABELS: Record<string, string> = { twin: 'Twin', full: 'Full', queen: 'Queen', king: 'King' };
const DUSTSKIRT_STYLE_LABELS: Record<string, string> = { shirred: 'Shirred', tailored: 'Tailored', box_pleated: 'Box Pleated' };

// Hardware allowance deducted from Floor-to-Ceiling to get Finished Length —
// see CLAUDE.md "OUTPUTS THAT ARE NOT ON QUOTE BUT NEEDED FOR WORKORDER".
const HARDWARE_ALLOWANCE_INCHES: Record<string, number> = {
  hospital_track: 1.5, rod_and_rings: 3.5, ctm: 0, traversing_rod: 1.5,
};

function fmtNum(n: number | null | undefined): string {
  return n != null ? String(Number(n)) : '';
}

// ── Work order types ─────────────────────────────────────────────────────────

type WorkOrderType = 'drapery' | 'roman_shade' | 'roman_valance' | 'pleated_valance' | 'tailored_valance' | 'shirred_valance' | 'roller_shade' | 'austrian_shade' | 'balloon_shade' | 'pillow' | 'sham' | 'bolster_pillow' | 'cushion' | 'dustskirt' | 'box_spring_cover' | 'duvet' | 'coverlet' | 'tablecloth_round' | 'tablecloth_rectangle' | 'furniture' | 'bench' | 'cornice' | 'bed';

const TYPE_LABELS: Record<WorkOrderType, string> = {
  drapery: 'Drapery',
  roman_shade: 'Roman Shade',
  roman_valance: 'Roman Valance',
  pleated_valance: 'Pleated Valance',
  tailored_valance: 'Tailored Valance',
  shirred_valance: 'Shirred Valance',
  roller_shade: 'Roller Shade',
  austrian_shade: 'Austrian Shade',
  balloon_shade: 'Balloon Shade',
  pillow: 'Pillow',
  sham: 'Sham',
  bolster_pillow: 'Bolster Pillow',
  cushion: 'Cushion',
  dustskirt: 'Dustskirt',
  box_spring_cover: 'Box Spring Cover',
  duvet: 'Duvet',
  coverlet: 'Coverlet',
  tablecloth_round: 'Tablecloth (Round)',
  tablecloth_rectangle: 'Tablecloth (Rectangle)',
  furniture: 'Furniture',
  bench: 'Bench',
  cornice: 'Cornice',
  bed: 'Bed',
};

// traversing_drape, side_panel, and anything else not called out below all
// fall into the generic 'drapery' bucket. pillow, sham, bolster pillow,
// cushion, dustskirt, box_spring_cover, duvet, coverlet, and both tablecloth
// types all have dedicated tabs, per their SOPs' identically-worded "Work
// Order Outputs" sections.
function itemTypeToWO(itemType: string | null): WorkOrderType {
  switch (itemType) {
    case 'roman_shade':      return 'roman_shade';
    case 'roman_valance':    return 'roman_valance';
    case 'pleated_valance':  return 'pleated_valance';
    case 'tailored_valance': return 'tailored_valance';
    case 'shirred_valance':  return 'shirred_valance';
    case 'roller_shade':     return 'roller_shade';
    case 'austrian_shade':   return 'austrian_shade';
    case 'balloon_shade':    return 'balloon_shade';
    case 'pillow':           return 'pillow';
    case 'sham':             return 'sham';
    case 'bolster_pillow':   return 'bolster_pillow';
    case 'cushion':          return 'cushion';
    case 'dustskirt':        return 'dustskirt';
    case 'box_spring_cover': return 'box_spring_cover';
    case 'duvet':             return 'duvet';
    case 'coverlet':          return 'coverlet';
    case 'tablecloth_round':      return 'tablecloth_round';
    case 'tablecloth_rectangle':  return 'tablecloth_rectangle';
    case 'furniture':        return 'furniture';
    case 'bench':            return 'bench';
    case 'cornice':          return 'cornice';
    case 'bed':              return 'bed';
    default:                 return 'drapery';
  }
}

// ── Traveler row (drapery + roman shade) ─────────────────────────────────────

interface TravelerRow {
  tempId: string;
  draftQuoteObjectId: number | null;
  fabricColor: string;
  // Persisted as the work_order_line_item's `quantity` column (see
  // travelerRowToArgs) and displayed on the traveler as "Yds" — for this
  // row shape (drapery/roman shade/valance-family), `quantity` means
  // fabric yardage, not a unit count of objects. This is a real, on-screen
  // "Yds" column (WorkOrderDetail.tsx's ShadeTravelerTable/TravelerItems
  // read `item.quantity` directly for it, not a notes field), so don't
  // repurpose this field to mean unit count without also updating those
  // display columns — an earlier attempt at exactly that regressed the
  // on-screen "Yds" column to show the object's unit count instead.
  yds: string;
  // The draft object's own unit count (draft_quote_object.quantity) — a
  // genuinely different number from `yds` above. Carried through `notes`
  // (see travelerRowToArgs), not the `quantity` column, so it doesn't
  // collide with the existing Yds display. Only set on the first row of a
  // multi-row object (Fabric row) — blanked on subsequent rows (Trim, etc.)
  // so it isn't misread as a second batch, same convention numPairs/
  // widthPanel already use.
  qty: string;
  numPairs: string;
  widthPanel: string;
  rodFace: string;
  fw: string;
  fl: string;
  hemSize: string;
  returns: string;
  overlaps: string;
  line: string;
  interLine: string;
  roomWindow: string;
  hardware: string;
  motor: string;
  pleatType: string;
  // Roman shade only — blank for drape/side panel.
  board: string;
  flap: string;
}

function draperyAutoFill(widthInches: number): { numPairs: string; widthPanel: string } {
  const finishedWidth = widthInches + 16;
  const cuts = (finishedWidth * 3) / 54;
  const ceilCuts = Math.ceil(cuts);
  const evenCuts = ceilCuts % 2 === 0 ? ceilCuts : ceilCuts + 1;
  // Width/Panel mirrors # Prs — both are the same PPR figure (issue #59),
  // there's no separate "widths per panel" calculation in this codebase.
  const pairs = String(evenCuts / 2);
  return {
    numPairs: pairs,
    widthPanel: pairs,
  };
}

// Side panels use a width-bracket cut-count lookup, not drape's fullness
// formula — no +16 finished-width adjustment either.
function sidePanelAutoFill(widthInches: number): { numPairs: string; widthPanel: string } {
  const cuts = widthInches <= 36 ? 2 : widthInches < 60 ? 3 : 4;
  const evenCuts = cuts % 2 === 0 ? cuts : cuts + 1;
  // Width/Panel mirrors # Prs — see draperyAutoFill's comment above.
  const pairs = String(evenCuts / 2);
  return {
    numPairs: pairs,
    widthPanel: pairs,
  };
}

// Rod Face / Finished Width / Finished Length are never stored — they're
// derived here from the draft object's raw inputs (rodFaceInches/overlapInches/
// returnInches/heightInches/hardwareType) so nothing goes stale if those inputs
// are edited later. Falls back to the SOP defaults for objects saved before
// these fields existed (rodFace = width+16, overlap 3.5", return 4").
function drapeTravelerCalc(obj: DraftQuoteObject): { rodFace: string; fw: string; fl: string } {
  const rodFace = obj.rodFaceInches != null
    ? Number(obj.rodFaceInches)
    : (obj.widthInches != null ? Number(obj.widthInches) + 16 : null);
  const overlap = obj.overlapInches != null ? Number(obj.overlapInches) : 3.5;
  const ret = obj.returnInches != null ? Number(obj.returnInches) : 4;
  const fw = rodFace != null ? rodFace + (overlap * 2) + (ret * 2) : null;
  const allowance = obj.hardwareType ? (HARDWARE_ALLOWANCE_INCHES[obj.hardwareType] ?? 0) : 0;
  const fl = obj.heightInches != null ? obj.heightInches - allowance : null;
  return { rodFace: fmtNum(rodFace), fw: fmtNum(fw), fl: fmtNum(fl) };
}

// Cut count / "PPRs" — same formula romanShadeCalculations.ts uses
// server-side for its own Cuts figure (Cuts = ceil((Width + 6) ÷ Fabric
// Width), fabric width defaults to 54"). Roman Shade and Roman Valance both
// use this: it only depends on Width, which both types collect the same way.
function romanCutsAutoFill(widthInches: number | null | undefined, fabricWidthInches: number | null | undefined): string {
  if (widthInches == null) return '';
  const fabricWidth = fabricWidthInches != null ? Number(fabricWidthInches) : 54;
  const cuts = Math.ceil((Number(widthInches) + 6) / fabricWidth);
  return String(cuts);
}

// Finished Width/Length/Board/Flap are never stored — derived here from the
// object's raw inputs (widthInches/heightInches/mountType/spaceAboveWindowInches
// for Finished Width/Length; flapSizeInches, default 5", for Flap) so nothing
// goes stale if those inputs are edited later. Board spec reuses calcBoard()
// (built for Austrian/Balloon Shade — same 1×3/1×4, width−¼" formula the SOP
// calls for here too); unlike Roman Valance, Roman Shade also surfaces
// calcBoard()'s `returns` value (see objectToTravelerRows) since it's part
// of this type's documented work-order field set. `numPairs` is the cut
// count from romanCutsAutoFill, filling the same "# Prs"/PPR role side
// panel's Fabrication label plays.
function romanShadeTravelerCalc(obj: DraftQuoteObject): { finishedWidth: string; finishedLength: string; board: string; flap: string; returns: string; numPairs: string } {
  const finishedWidth = obj.widthInches;
  // Number(...) here isn't redundant — numeric columns come back from
  // GraphQL as strings despite the DraftQuoteObject interface's `number`
  // annotation, and `+` silently does string concatenation (not addition)
  // when either operand is a string (e.g. 20 + "3.6" === "203.6").
  const finishedLength = obj.mountType === 'outside' && obj.heightInches != null
    ? Number(obj.heightInches) + Number(obj.spaceAboveWindowInches ?? 0)
    : obj.heightInches;
  const { board, returns } = calcBoard(obj.widthInches);
  const flap = obj.flapSizeInches != null ? `${Number(obj.flapSizeInches)}"` : '5"';
  const numPairs = romanCutsAutoFill(obj.widthInches, obj.fabricWidthInches);
  return { finishedWidth: fmtNum(finishedWidth), finishedLength: fmtNum(finishedLength), board, flap, returns, numPairs };
}

// Finished Width/Length/Board/Returns/Flap are never stored — derived here
// from the object's raw inputs, same convention as romanShadeTravelerCalc,
// but Roman Valance's length is always the fixed 20"/20"+space-above formula
// (romanValanceCalculations.ts), never obj.heightInches — the valance form
// never collects a Length input at all. Returns was previously discarded
// here (calcBoard() computed it but objectToTravelerRows blanked it back
// out) since Roman Valance's Work Order Outputs spec didn't call it out as
// its own field — since corrected to match Roman Shade/Pleated/Tailored/
// Shirred Valance, which all surface it.
function romanValanceTravelerCalc(obj: DraftQuoteObject): { finishedWidth: string; finishedLength: string; board: string; returns: string; flap: string; numPairs: string } {
  const finishedWidth = obj.widthInches;
  // Number(...) is required, not defensive style — numeric GraphQL columns
  // come back as strings despite the interface's `number` annotation, and
  // `+` does string concatenation when either side is a string
  // (20 + "3.6" === "203.6", not 23.6 — this is exactly the bug that shipped
  // here before this fix).
  const finishedLength = obj.mountType === 'outside'
    ? 20 + Number(obj.spaceAboveWindowInches ?? 0)
    : 20;
  const { board, returns } = calcBoard(obj.widthInches);
  const flap = obj.flapSizeInches != null ? `${Number(obj.flapSizeInches)}"` : '5"';
  const numPairs = romanCutsAutoFill(obj.widthInches, obj.fabricWidthInches);
  return { finishedWidth: fmtNum(finishedWidth), finishedLength: fmtNum(finishedLength), board, returns, flap, numPairs };
}

// Finished Width/Length/Board/Returns are never stored — derived here from
// the object's raw inputs, same convention as romanValanceTravelerCalc
// (Width used verbatim for Finished Width on both IM and OM — "Sill Width"
// in the SOP doc is just the same widthInches value, there's no separate
// sill-width input anywhere). Unlike Roman Valance, Pleated Valance has no
// Flap field at all (not part of this SOP) and its Work Order spec calls out
// Returns as its own field (CLAUDE.md "Work Order Outputs — Pleated
// Valance"), so this surfaces calcBoard()'s `returns` value instead of
// discarding it the way romanValanceTravelerCalc does.
//
// Reused verbatim for Tailored Valance too (see objectToTravelerRows below):
// Tailored Valance and Shirred Valance are separate item types from Pleated
// Valance in the database (own enum value, own rates, own calc file), but
// this traveler derivation has no rates in it at all — it's the exact same
// formula (Finished Width = width, Finished Length = 20"/20"+space above,
// Board/Returns via calcBoard) for all three, so duplicating it here would
// just be dead-weight code that could drift.
function pleatedValanceTravelerCalc(obj: DraftQuoteObject): { finishedWidth: string; finishedLength: string; board: string; returns: string } {
  const finishedWidth = obj.widthInches;
  const finishedLength = obj.mountType === 'outside'
    ? 20 + Number(obj.spaceAboveWindowInches ?? 0)
    : 20;
  const { board, returns } = calcBoard(obj.widthInches);
  return { finishedWidth: fmtNum(finishedWidth), finishedLength: fmtNum(finishedLength), board, returns };
}

// Traversing drape and side panel explode into one row per fabric-type
// draft_quote_object_item (Fabric/Lining/Interlining/Velcro/Trim). Roman
// Shade/Valance use the same per-object shared-field mechanism but collapse
// down to just Fabric + Trim rows (see the filter below) — all sharing that
// object's own work-order-only fields (Rod Face/F.W./F.L./Hem/Returns/
// Overlaps/Board/Flap/Hardware/Motor/Pleat Type), since those are
// per-object, not per-fabric-row, attributes.
function objectToTravelerRows(obj: DraftQuoteObject, workOrderType: WorkOrderType): TravelerRow[] {
  const line = obj.liningType ? (LINING_LABELS[obj.liningType] || obj.liningType) : '';
  const hardware = obj.hardwareType ? (HARDWARE_LABELS[obj.hardwareType] || obj.hardwareType) : '';
  const motor = obj.operatingSystem ? (OS_LABELS[obj.operatingSystem] || obj.operatingSystem) : '';
  const roomWindow = obj.name || '';
  const interLine = obj.interliningType && obj.interliningType !== 'none'
    ? (INTERLINING_LABELS[obj.interliningType] || obj.interliningType)
    : (obj.bumpInterlining ? 'Bump' : '');

  let shared: Omit<TravelerRow, 'tempId' | 'draftQuoteObjectId' | 'fabricColor' | 'yds' | 'qty'>;

  if (workOrderType === 'pleated_valance' || workOrderType === 'tailored_valance' || workOrderType === 'shirred_valance') {
    const { finishedWidth, finishedLength, board, returns } = pleatedValanceTravelerCalc(obj);
    shared = {
      numPairs: '', widthPanel: '',
      rodFace: '', fw: finishedWidth, fl: finishedLength,
      hemSize: '', returns, overlaps: '',
      line, interLine, roomWindow, hardware, motor, pleatType: '',
      board, flap: '',
    };
  } else if (workOrderType === 'roman_shade' || workOrderType === 'roman_valance') {
    const calc = workOrderType === 'roman_valance'
      ? romanValanceTravelerCalc(obj)
      : romanShadeTravelerCalc(obj);
    const { finishedWidth, finishedLength, board, flap, returns, numPairs } = calc;
    shared = {
      numPairs, widthPanel: '',
      rodFace: '', fw: finishedWidth, fl: finishedLength,
      hemSize: '', returns, overlaps: '',
      line, interLine, roomWindow, hardware, motor, pleatType: '',
      board, flap,
    };
  } else {
    const drapeCalc = drapeTravelerCalc(obj);
    const drapery = obj.widthInches == null ? null
      : obj.itemType === 'side_panel' ? sidePanelAutoFill(obj.widthInches)
      : draperyAutoFill(obj.widthInches);
    const hemSize = obj.hemSize || '';
    const returns = obj.returnInches != null ? `${Number(obj.returnInches)}"` : '';
    const overlaps = obj.overlapInches != null ? `${Number(obj.overlapInches)}"` : '';
    const pleatType = obj.pleatType ? (PLEAT_LABELS[obj.pleatType] || obj.pleatType) : '';
    shared = {
      numPairs: drapery?.numPairs ?? '', widthPanel: drapery?.widthPanel ?? '', ...drapeCalc,
      hemSize, returns, overlaps, line, interLine, hardware, motor, pleatType, roomWindow,
      board: '', flap: '',
    };
  }

  // Roman Shade/Valance: collapse the 5 possible fabric-section rows
  // (Fabric/Lining/Interlining/Velcro/Trim, per romanShadeCalculations.ts)
  // down to just Fabric + Trim (if any) — Lining/Interlining/Velcro amounts
  // are estimated at quoting time and the real amounts are obvious to the
  // operator on the floor, so they don't need their own traveler rows.
  // Drapery/Side Panel (per issue #59) get the same treatment — up to 4
  // rows (Fabric/Lining/Interlining/Trim, per traversingDrapeCalculations.ts
  // — no Velcro line for this type) collapsed to Fabric + Trim. Fabric and
  // Trim descriptions are user-typed (fabric/trim description inputs,
  // defaulting to 'Fabric'/'Trim'), so they're kept by excluding the fixed,
  // always-literal Lining/Interlining/Velcro descriptions instead of
  // matching on name — safe because the server always emits exactly this
  // set of fabric-section descriptions for these types, in this order
  // (Fabric, then Trim last), so no re-sorting is needed after filtering.
  // Every fabric-section description is now prefixed "Yards of " (see
  // yardsOf() in sopCalculations.ts) — the literals below are matched
  // prefixed too.
  const isRomanType = workOrderType === 'roman_shade' || workOrderType === 'roman_valance';
  const isDrapeType = workOrderType === 'drapery';
  const fabricItems = (obj.draftQuoteObjectItemsByDraftQuoteObjectId?.nodes || [])
    .filter((i) => i.section === 'fabric' && !i.archivedAt)
    .filter((i) => !(isRomanType || isDrapeType) || (i.description !== 'Yards of Lining' && i.description !== 'Yards of Velcro' && !i.description.startsWith('Yards of Interlining')));

  if (fabricItems.length === 0) {
    // No priced fabric rows yet — still produce one row so the object is usable.
    return [{
      tempId: Math.random().toString(36).slice(2),
      draftQuoteObjectId: obj.id,
      fabricColor: obj.fabricDescription || '',
      yds: '',
      qty: fmtNum(obj.quantity),
      ...shared,
    }];
  }

  return fabricItems.map((item, idx) => ({
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: obj.id,
    fabricColor: item.description,
    yds: String(Number(item.quantity)),
    qty: idx === 0 ? fmtNum(obj.quantity) : '',
    ...shared,
    // Roman Shade/Valance and Drapery/Side Panel: # Prs (and, for drapery,
    // Width/Panel — the same PPR figure) describe the object as a whole
    // (a fabric-width cut count, not a per-row quantity) — show it once, on
    // the first (Fabric) row, rather than repeating it on the Trim row too
    // (issue #59: avoids misreading the repeated number as a second batch).
    ...((isRomanType || isDrapeType) && idx > 0 ? { numPairs: '', widthPanel: '' } : {}),
  }));
}

function travelerRowToArgs(row: TravelerRow, index: number) {
  const notes = [
    row.qty         && `Qty: ${row.qty}`,
    row.numPairs    && `Prs: ${row.numPairs}`,
    row.widthPanel  && `Width/Panel: ${row.widthPanel}`,
    row.rodFace     && `Rod Face: ${row.rodFace}`,
    row.fw          && `F.W.: ${row.fw}`,
    row.fl          && `F.L.: ${row.fl}`,
    row.hemSize     && `Hem: ${row.hemSize}`,
    row.returns     && `Returns: ${row.returns}`,
    row.overlaps    && `Overlaps: ${row.overlaps}`,
    row.line        && `Line: ${row.line}`,
    row.interLine   && `Inter-Line: ${row.interLine}`,
    row.roomWindow  && `Room/Window: ${row.roomWindow}`,
    row.hardware    && `Hardware: ${row.hardware}`,
    row.motor       && `Operating System: ${row.motor}`,
    row.pleatType   && `Pleat Type: ${row.pleatType}`,
    row.board       && `Board: ${row.board}`,
    row.flap        && `Flap: ${row.flap}`,
  ].filter(Boolean).join(' | ');
  return {
    itemNumber: index + 1,
    description: row.fabricColor || '—',
    quantity: parseFloat(row.yds) || 1,
    notes: notes || null,
    quoteLineItemId: null,
    draftQuoteObjectId: row.draftQuoteObjectId,
  };
}

// ── Austrian / Balloon shade row ─────────────────────────────────────────────
// Both shade types share identical work order columns per their markdown specs.

interface ShadeRow {
  tempId: string;
  draftQuoteObjectId: number | null;
  roomWindow: string;
  width: string;
  length: string;
  mountType: string;
  fabric: string;
  lining: string;
  interlining: string;
  trim: string;
  motor: string;
  board: string;
  returns: string;
}

function calcBoard(widthInches: number | null | undefined): { board: string; returns: string } {
  if (widthInches == null) return { board: '', returns: '' };
  const w = Number(widthInches);
  // Three width tiers: ≤60" → 1×3, 60–96" → 1×4, >96" → 1×5. The 96"
  // breakpoint reuses the board-*pricing* breakpoint already used elsewhere
  // in the codebase (e.g. roman_valance_board_small_cost/large_cost) as the
  // new 1×4/1×5 display breakpoint — Returns follows the same
  // board-nominal-size-minus-½" pattern as the existing two tiers
  // (2.5"/3.5") extended to a third (4.5").
  const type = w <= 60 ? '1×3' : w <= 96 ? '1×4' : '1×5';
  const len = w - 0.25;
  const lenStr = Number.isInteger(len) ? String(len) : len.toFixed(2).replace(/\.?0+$/, '');
  const returns = w <= 60 ? '2.5"' : w <= 96 ? '3.5"' : '4.5"';
  return { board: `${type}, ${lenStr}"`, returns };
}

function objectToShadeRow(obj: DraftQuoteObject): ShadeRow {
  const { board, returns } = calcBoard(obj.widthInches);
  // Finished Length differs by mount type for both Balloon and Austrian
  // Shade — Outside Mount adds the Space Above Window measurement ("top of
  // window to sill plus the distance above the window" per Austrian Shade's
  // SOP), same convention Roman Shade's OM finished length uses. Finished
  // Width has no such adjustment for either type — the SOP's "Sill Width"
  // (OM) vs. "total width, no deductions" (IM) language is a semantic
  // distinction in what the Width input represents, not a formula.
  // obj.heightInches/spaceAboveWindowInches come back from GraphQL as strings
  // (PostGraphile serializes numeric columns as strings to avoid precision
  // loss) despite the DraftQuoteObject type above claiming `number | null` —
  // adding them with `+` before converting produces string concatenation
  // (e.g. "51.00" + "4.5" → "51.004.5"), which Number() can't parse back into
  // a valid number, showing up as "NaN" on the traveler. Convert each side to
  // a real number first.
  const length = (obj.itemType === 'balloon_shade' || obj.itemType === 'austrian_shade') && obj.mountType === 'outside'
    ? fmtNum((Number(obj.heightInches) || 0) + (Number(obj.spaceAboveWindowInches) || 0))
    : fmtNum(obj.heightInches);
  return {
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: obj.id,
    roomWindow: obj.name || '',
    width: fmtNum(obj.widthInches),
    length,
    mountType: obj.mountType ? (obj.mountType === 'inside' ? 'IM' : 'OM') : '',
    fabric: obj.fabricDescription || '',
    lining: obj.liningType ? (LINING_LABELS[obj.liningType] || obj.liningType) : '',
    interlining: obj.bumpInterlining ? 'Flannel' : '',
    trim: obj.trimPlacement && obj.trimPlacement !== 'none' ? 'Yes' : '',
    motor: obj.operatingSystem ? (OS_LABELS[obj.operatingSystem] || obj.operatingSystem) : '',
    board,
    returns,
  };
}

function shadeRowToArgs(row: ShadeRow, index: number) {
  const notes = [
    row.width       && `Width: ${row.width}`,
    row.length      && `Length: ${row.length}`,
    row.mountType   && `Mount: ${row.mountType}`,
    row.fabric      && `Fabric: ${row.fabric}`,
    row.lining      && `Lining: ${row.lining}`,
    row.interlining && `Interlining: ${row.interlining}`,
    row.trim        && `Trim: ${row.trim}`,
    row.motor       && `Operating System: ${row.motor}`,
    row.board       && `Board: ${row.board}`,
    row.returns     && `Returns: ${row.returns}`,
  ].filter(Boolean).join(' | ');
  return {
    itemNumber: index + 1,
    description: row.roomWindow || `Shade ${index + 1}`,
    quantity: 1,
    notes: notes || null,
    quoteLineItemId: null,
    draftQuoteObjectId: row.draftQuoteObjectId,
  };
}

// ── Roller shade row ──────────────────────────────────────────────────────────

interface RollerRow {
  tempId: string;
  draftQuoteObjectId: number | null;
  roomWindow: string;
  windowWidth: string;
  windowLength: string;
  tubeSize: string;
  fabric: string;
  drive: string;
  motor: string;
  rollDirection: string;
  rtOrLt: string;
  insideOutside: string;
}

function calcTubeSize(widthInches: number | null | undefined, mountType: string | null | undefined): string {
  if (widthInches == null) return '';
  const cut = mountType === 'inside' ? Number(widthInches) - 1.25 : Number(widthInches) + 6;
  if (cut <= 60) return '32';
  if (cut <= 96) return '38';
  return '50';
}

function objectToRollerRow(obj: DraftQuoteObject): RollerRow {
  return {
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: obj.id,
    roomWindow: obj.name || '',
    windowWidth: fmtNum(obj.widthInches),
    windowLength: fmtNum(obj.heightInches),
    tubeSize: calcTubeSize(obj.widthInches, obj.mountType),
    fabric: obj.fabricDescription || '',
    drive: obj.chainColor || '',
    motor: obj.operatingSystem ? (OS_LABELS[obj.operatingSystem] || obj.operatingSystem) : '',
    rollDirection: obj.rollDirection || '',
    rtOrLt: obj.driveSide ? obj.driveSide.toUpperCase() : '',
    insideOutside: obj.mountType || '',
  };
}

function emptyRollerRow(): RollerRow {
  return {
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: null,
    roomWindow: '', windowWidth: '', windowLength: '', tubeSize: '',
    fabric: '', drive: '', motor: '', rollDirection: '', rtOrLt: '', insideOutside: '',
  };
}

function rollerRowToArgs(row: RollerRow, index: number) {
  const notes = [
    row.windowWidth    && `Window Width: ${row.windowWidth}`,
    row.windowLength   && `Window Length: ${row.windowLength}`,
    row.tubeSize       && `Tube Size: ${row.tubeSize}`,
    row.fabric         && `Fabric: ${row.fabric}`,
    row.drive          && `Drive: ${row.drive}`,
    row.motor          && `Operating System: ${row.motor}`,
    row.rollDirection  && `Roll Direction: ${row.rollDirection}`,
    row.rtOrLt         && `RT or LT: ${row.rtOrLt}`,
    row.insideOutside  && `Inside/Outside: ${row.insideOutside}`,
  ].filter(Boolean).join(' | ');
  return {
    itemNumber: index + 1,
    description: row.roomWindow || `Shade ${index + 1}`,
    quantity: 1,
    notes: notes || null,
    quoteLineItemId: null,
    draftQuoteObjectId: row.draftQuoteObjectId,
  };
}

// ── Pillow row ─────────────────────────────────────────────────────────────────
// Per Quoting_markdown/pillows.md's "Work Order Outputs": pillow travelers
// deliberately do NOT use the drapery/roman-shade box layout — just
// Quantity, Size, Zipper, Insert, Fabric, and Welt/Trim/Ruffle.

interface PillowRow {
  tempId: string;
  draftQuoteObjectId: number | null;
  roomWindow: string;
  quantity: string;
  size: string;
  zipper: string;
  insert: string;
  fabric: string;
  weltTrimRuffle: string;
  contrastWeltFabric: string;
  trimName: string;
}

// Mirrors calcPillow's insert-size formula (pillowCalculations.ts):
// max(20, min(26, roundUp(max(width, length) + 2, nearest even inch))).
function calcPillowInsertSize(widthInches: number | null, heightInches: number | null): number {
  const w = widthInches ?? 0;
  const h = heightInches ?? 0;
  const raw = Math.max(w, h) + 2;
  const evenUp = Math.ceil(raw) % 2 === 0 ? Math.ceil(raw) : Math.ceil(raw) + 1;
  return Math.max(20, Math.min(26, evenUp));
}

function objectToPillowRow(obj: DraftQuoteObject): PillowRow {
  const weltTrimRuffle = [
    obj.weltType && obj.weltType !== 'none' ? (WELT_LABELS[obj.weltType] || obj.weltType) : null,
    obj.trimPlacement && obj.trimPlacement !== 'none' ? 'Trim' : null,
    obj.ruffle ? 'Ruffle' : null,
  ].filter(Boolean).join(', ');
  const insertSize = calcPillowInsertSize(obj.widthInches, obj.heightInches);
  return {
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: obj.id,
    roomWindow: obj.name || '',
    quantity: fmtNum(obj.quantity),
    size: obj.widthInches != null && obj.heightInches != null ? `${fmtNum(obj.widthInches)}" × ${fmtNum(obj.heightInches)}"` : '',
    zipper: fmtNum(obj.quantity),
    insert: obj.insertType ? `${insertSize}" ${INSERT_LABELS[obj.insertType] || obj.insertType}` : '',
    fabric: obj.fabricDescription || '',
    weltTrimRuffle,
    contrastWeltFabric: obj.weltType === 'contrast' ? (obj.contrastWeltFabricDescription || '') : '',
    trimName: obj.trimPlacement && obj.trimPlacement !== 'none' ? (obj.trimDescription || '') : '',
  };
}

function pillowRowToArgs(row: PillowRow, index: number) {
  const notes = [
    row.size               && `Size: ${row.size}`,
    row.zipper             && `Zipper: ${row.zipper}`,
    row.insert             && `Insert: ${row.insert}`,
    row.fabric             && `Fabric: ${row.fabric}`,
    row.weltTrimRuffle     && `Welt/Trim/Ruffle: ${row.weltTrimRuffle}`,
    row.contrastWeltFabric && `Contrast Welt Fabric: ${row.contrastWeltFabric}`,
    row.trimName           && `Trim Name: ${row.trimName}`,
  ].filter(Boolean).join(' | ');
  return {
    itemNumber: index + 1,
    description: row.roomWindow || `Pillow ${index + 1}`,
    quantity: parseFloat(row.quantity) || 1,
    notes: notes || null,
    quoteLineItemId: null,
    draftQuoteObjectId: row.draftQuoteObjectId,
  };
}

// ── Sham row ───────────────────────────────────────────────────────────────────
// Per Quoting_markdown/shams.md's "Work Order Outputs" (identical wording to
// pillow's) — same 5 fields as pillow's traveler, but Size/Insert read from
// the named size bucket (Standard/Euro/Queen/King) rather than computed
// inches, so this is a separate row/component instead of branching
// PillowRow on item type.

interface ShamRow {
  tempId: string;
  draftQuoteObjectId: number | null;
  roomWindow: string;
  quantity: string;
  size: string;
  zipper: string;
  insert: string;
  fabric: string;
  weltTrimRuffle: string;
  contrastWeltFabric: string;
  trimName: string;
}

function objectToShamRow(obj: DraftQuoteObject): ShamRow {
  const weltTrimRuffle = [
    obj.weltType && obj.weltType !== 'none' ? (WELT_LABELS[obj.weltType] || obj.weltType) : null,
    obj.trimPlacement && obj.trimPlacement !== 'none' ? 'Trim' : null,
    obj.ruffle ? 'Ruffle' : null,
  ].filter(Boolean).join(', ');
  const sizeLabel = obj.shamSize ? (SHAM_SIZE_LABELS[obj.shamSize] || obj.shamSize) : '';
  return {
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: obj.id,
    roomWindow: obj.name || '',
    quantity: fmtNum(obj.quantity),
    size: sizeLabel,
    zipper: fmtNum(obj.quantity),
    insert: obj.insertType ? `${sizeLabel} ${INSERT_LABELS[obj.insertType] || obj.insertType}` : '',
    fabric: obj.fabricDescription || '',
    weltTrimRuffle,
    contrastWeltFabric: obj.weltType === 'contrast' ? (obj.contrastWeltFabricDescription || '') : '',
    trimName: obj.trimPlacement && obj.trimPlacement !== 'none' ? (obj.trimDescription || '') : '',
  };
}

function shamRowToArgs(row: ShamRow, index: number) {
  const notes = [
    row.size               && `Size: ${row.size}`,
    row.zipper             && `Zipper: ${row.zipper}`,
    row.insert             && `Insert: ${row.insert}`,
    row.fabric             && `Fabric: ${row.fabric}`,
    row.weltTrimRuffle     && `Welt/Trim/Ruffle: ${row.weltTrimRuffle}`,
    row.contrastWeltFabric && `Contrast Welt Fabric: ${row.contrastWeltFabric}`,
    row.trimName           && `Trim Name: ${row.trimName}`,
  ].filter(Boolean).join(' | ');
  return {
    itemNumber: index + 1,
    description: row.roomWindow || `Sham ${index + 1}`,
    quantity: parseFloat(row.quantity) || 1,
    notes: notes || null,
    quoteLineItemId: null,
    draftQuoteObjectId: row.draftQuoteObjectId,
  };
}

// ── Bolster Pillow row ────────────────────────────────────────────────────────
// Per Quoting_markdown/bolster_pillows.md's "Work Order Outputs" (identical
// wording to pillow's/sham's) — same 5 fields, Size shown as Length × Diameter
// (unlike sham's named size bucket) and Insert shown by type label only (no
// computed size — bolster inserts are priced by formula, not a size lookup).

interface BolsterRow {
  tempId: string;
  draftQuoteObjectId: number | null;
  roomWindow: string;
  quantity: string;
  size: string;
  zipper: string;
  insert: string;
  fabric: string;
  weltTrimRuffle: string;
  contrastWeltFabric: string;
  trimName: string;
}

function objectToBolsterRow(obj: DraftQuoteObject): BolsterRow {
  const weltTrimRuffle = [
    obj.weltType && obj.weltType !== 'none' ? (WELT_LABELS[obj.weltType] || obj.weltType) : null,
    obj.trimPlacement && obj.trimPlacement !== 'none' ? 'Trim' : null,
    obj.ruffle ? 'Ruffle' : null,
  ].filter(Boolean).join(', ');
  return {
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: obj.id,
    roomWindow: obj.name || '',
    quantity: fmtNum(obj.quantity),
    size: obj.widthInches != null && obj.heightInches != null ? `${fmtNum(obj.widthInches)}" L × ${fmtNum(obj.heightInches)}" D` : '',
    zipper: fmtNum(obj.quantity),
    insert: obj.insertType ? (INSERT_LABELS[obj.insertType] || obj.insertType) : '',
    fabric: obj.fabricDescription || '',
    weltTrimRuffle,
    contrastWeltFabric: obj.weltType === 'contrast' ? (obj.contrastWeltFabricDescription || '') : '',
    trimName: obj.trimPlacement && obj.trimPlacement !== 'none' ? (obj.trimDescription || '') : '',
  };
}

function bolsterRowToArgs(row: BolsterRow, index: number) {
  const notes = [
    row.size               && `Size: ${row.size}`,
    row.zipper             && `Zipper: ${row.zipper}`,
    row.insert             && `Insert: ${row.insert}`,
    row.fabric             && `Fabric: ${row.fabric}`,
    row.weltTrimRuffle     && `Welt/Trim/Ruffle: ${row.weltTrimRuffle}`,
    row.contrastWeltFabric && `Contrast Welt Fabric: ${row.contrastWeltFabric}`,
    row.trimName           && `Trim Name: ${row.trimName}`,
  ].filter(Boolean).join(' | ');
  return {
    itemNumber: index + 1,
    description: row.roomWindow || `Bolster ${index + 1}`,
    quantity: parseFloat(row.quantity) || 1,
    notes: notes || null,
    quoteLineItemId: null,
    draftQuoteObjectId: row.draftQuoteObjectId,
  };
}

// ── Cushion row ────────────────────────────────────────────────────────────────
// Per Quoting_markdown/cushions.md's "Work Order Outputs" — Quantity, Size,
// Zipper, Insert, Fabric, Welt/Trim (no Ruffle; cushion's SOP doesn't offer
// one). Mirrors BolsterRow exactly minus the Ruffle field.

interface CushionRow {
  tempId: string;
  draftQuoteObjectId: number | null;
  roomWindow: string;
  quantity: string;
  size: string;
  zipper: string;
  insert: string;
  fabric: string;
  weltTrim: string;
}

function objectToCushionRow(obj: DraftQuoteObject): CushionRow {
  const weltTrim = [
    obj.weltType && obj.weltType !== 'none' ? (WELT_LABELS[obj.weltType] || obj.weltType) : null,
    obj.trimPlacement && obj.trimPlacement !== 'none' ? 'Trim' : null,
  ].filter(Boolean).join(', ');
  return {
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: obj.id,
    roomWindow: obj.name || '',
    quantity: fmtNum(obj.quantity),
    size: obj.widthInches != null && obj.heightInches != null && obj.depthInches != null
      ? `${fmtNum(obj.widthInches)}" L × ${fmtNum(obj.heightInches)}" D × ${fmtNum(obj.depthInches)}" T`
      : '',
    zipper: fmtNum(obj.quantity),
    insert: obj.insertType ? (INSERT_LABELS[obj.insertType] || obj.insertType) : '',
    fabric: obj.fabricDescription || '',
    weltTrim,
  };
}

function cushionRowToArgs(row: CushionRow, index: number) {
  const notes = [
    row.size     && `Size: ${row.size}`,
    row.zipper   && `Zipper: ${row.zipper}`,
    row.insert   && `Insert: ${row.insert}`,
    row.fabric   && `Fabric: ${row.fabric}`,
    row.weltTrim && `Welt/Trim: ${row.weltTrim}`,
  ].filter(Boolean).join(' | ');
  return {
    itemNumber: index + 1,
    description: row.roomWindow || `Cushion ${index + 1}`,
    quantity: parseFloat(row.quantity) || 1,
    notes: notes || null,
    quoteLineItemId: null,
    draftQuoteObjectId: row.draftQuoteObjectId,
  };
}

// ── Dustskirt row ──────────────────────────────────────────────────────────────
// Per Quoting_markdown/dustskirts.md's "Work Order Outputs" — Quantity, Bed
// Size, Style, Fabric, and a Drop box; "does not use the standard
// drapery/roman shade traveler format."

interface DustskirtRow {
  tempId: string;
  draftQuoteObjectId: number | null;
  roomWindow: string;
  quantity: string;
  bedSize: string;
  style: string;
  fabric: string;
  drop: string;
}

function objectToDustskirtRow(obj: DraftQuoteObject): DustskirtRow {
  return {
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: obj.id,
    roomWindow: obj.name || '',
    quantity: fmtNum(obj.quantity),
    bedSize: obj.bedSize ? (BED_SIZE_LABELS[obj.bedSize] || obj.bedSize) : '',
    style: obj.dustskirtStyle ? (DUSTSKIRT_STYLE_LABELS[obj.dustskirtStyle] || obj.dustskirtStyle) : '',
    fabric: obj.fabricDescription || '',
    drop: fmtNum(obj.dropInches),
  };
}

function dustskirtRowToArgs(row: DustskirtRow, index: number) {
  const notes = [
    row.bedSize && `Bed Size: ${row.bedSize}`,
    row.style   && `Style: ${row.style}`,
    row.fabric  && `Fabric: ${row.fabric}`,
    row.drop    && `Drop: ${row.drop}`,
  ].filter(Boolean).join(' | ');
  return {
    itemNumber: index + 1,
    description: row.roomWindow || `Dustskirt ${index + 1}`,
    quantity: parseFloat(row.quantity) || 1,
    notes: notes || null,
    quoteLineItemId: null,
    draftQuoteObjectId: row.draftQuoteObjectId,
  };
}

// ── Box Spring Cover row ─────────────────────────────────────────────────────
// Per Quoting_markdown/box_spring_covers.md's "Work Order Outputs" —
// Quantity, Bed Size, Fabric, plus a "Box Spring Length/Width/Height" field.
// L/W/H is collected on the draft quote form (reusing the generic
// widthInches/heightInches/depthInches columns) when known at quote time,
// pre-filling this field — still editable here for shops that only find the
// dimensions out later, at install/fabrication time.

interface BoxSpringRow {
  tempId: string;
  draftQuoteObjectId: number | null;
  roomWindow: string;
  quantity: string;
  bedSize: string;
  fabric: string;
  lwh: string;
}

function objectToBoxSpringRow(obj: DraftQuoteObject): BoxSpringRow {
  return {
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: obj.id,
    roomWindow: obj.name || '',
    quantity: fmtNum(obj.quantity),
    bedSize: obj.bedSize ? (BED_SIZE_LABELS[obj.bedSize] || obj.bedSize) : '',
    fabric: obj.fabricDescription || '',
    // Pre-filled from the quote's L/W/H inputs when collected there; still a
    // free-text field here, editable before the work order is saved, for
    // shops that only find these dimensions out later.
    lwh: obj.widthInches != null && obj.heightInches != null && obj.depthInches != null
      ? `${fmtNum(obj.widthInches)}" L × ${fmtNum(obj.heightInches)}" W × ${fmtNum(obj.depthInches)}" H`
      : '',
  };
}

function boxSpringRowToArgs(row: BoxSpringRow, index: number) {
  const notes = [
    row.bedSize && `Bed Size: ${row.bedSize}`,
    row.fabric  && `Fabric: ${row.fabric}`,
    row.lwh     && `L/W/H: ${row.lwh}`,
  ].filter(Boolean).join(' | ');
  return {
    itemNumber: index + 1,
    description: row.roomWindow || `Box Spring Cover ${index + 1}`,
    quantity: parseFloat(row.quantity) || 1,
    notes: notes || null,
    quoteLineItemId: null,
    draftQuoteObjectId: row.draftQuoteObjectId,
  };
}

// ── Duvet row ────────────────────────────────────────────────────────────────
// Per Quoting_markdown/duvets.md's "Work Order Outputs" — Quantity, Size,
// Fabric, Self Welt (yes/no).

interface DuvetRow {
  tempId: string;
  draftQuoteObjectId: number | null;
  roomWindow: string;
  quantity: string;
  bedSize: string;
  fabric: string;
  selfWelt: string;
}

function objectToDuvetRow(obj: DraftQuoteObject): DuvetRow {
  return {
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: obj.id,
    roomWindow: obj.name || '',
    quantity: fmtNum(obj.quantity),
    bedSize: obj.bedSize ? (BED_SIZE_LABELS[obj.bedSize] || obj.bedSize) : '',
    fabric: obj.fabricDescription || '',
    selfWelt: obj.weltType === 'self' ? 'Yes' : 'No',
  };
}

function duvetRowToArgs(row: DuvetRow, index: number) {
  const notes = [
    row.bedSize  && `Size: ${row.bedSize}`,
    row.fabric   && `Fabric: ${row.fabric}`,
    row.selfWelt && `Self Welt: ${row.selfWelt}`,
  ].filter(Boolean).join(' | ');
  return {
    itemNumber: index + 1,
    description: row.roomWindow || `Duvet ${index + 1}`,
    quantity: parseFloat(row.quantity) || 1,
    notes: notes || null,
    quoteLineItemId: null,
    draftQuoteObjectId: row.draftQuoteObjectId,
  };
}

// ── Coverlet row ─────────────────────────────────────────────────────────────
// Per Quoting_markdown/coverlets.md's "Work Order Outputs" — Quantity, Size,
// Fabric.

interface CoverletRow {
  tempId: string;
  draftQuoteObjectId: number | null;
  roomWindow: string;
  quantity: string;
  bedSize: string;
  fabric: string;
}

function objectToCoverletRow(obj: DraftQuoteObject): CoverletRow {
  return {
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: obj.id,
    roomWindow: obj.name || '',
    quantity: fmtNum(obj.quantity),
    bedSize: obj.bedSize ? (BED_SIZE_LABELS[obj.bedSize] || obj.bedSize) : '',
    fabric: obj.fabricDescription || '',
  };
}

function coverletRowToArgs(row: CoverletRow, index: number) {
  const notes = [
    row.bedSize && `Size: ${row.bedSize}`,
    row.fabric  && `Fabric: ${row.fabric}`,
  ].filter(Boolean).join(' | ');
  return {
    itemNumber: index + 1,
    description: row.roomWindow || `Coverlet ${index + 1}`,
    quantity: parseFloat(row.quantity) || 1,
    notes: notes || null,
    quoteLineItemId: null,
    draftQuoteObjectId: row.draftQuoteObjectId,
  };
}

// ── Round Tablecloth row ─────────────────────────────────────────────────────
// Per Quoting_markdown/tablecloths.md's "Work Order Outputs" — Quantity,
// Size of table (diameter for round), Fabric. widthInches carries Diameter.

interface TableclothRoundRow {
  tempId: string;
  draftQuoteObjectId: number | null;
  roomWindow: string;
  quantity: string;
  size: string;
  fabric: string;
}

function objectToTableclothRoundRow(obj: DraftQuoteObject): TableclothRoundRow {
  return {
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: obj.id,
    roomWindow: obj.name || '',
    quantity: fmtNum(obj.quantity),
    size: obj.widthInches != null && obj.heightInches != null
      ? `${fmtNum(obj.widthInches)}" diameter × ${fmtNum(obj.heightInches)}" drop`
      : obj.widthInches != null ? `${fmtNum(obj.widthInches)}" diameter` : '',
    fabric: obj.fabricDescription || '',
  };
}

function tableclothRoundRowToArgs(row: TableclothRoundRow, index: number) {
  const notes = [
    row.size   && `Size: ${row.size}`,
    row.fabric && `Fabric: ${row.fabric}`,
  ].filter(Boolean).join(' | ');
  return {
    itemNumber: index + 1,
    description: row.roomWindow || `Tablecloth (Round) ${index + 1}`,
    quantity: parseFloat(row.quantity) || 1,
    notes: notes || null,
    quoteLineItemId: null,
    draftQuoteObjectId: row.draftQuoteObjectId,
  };
}

// ── Rectangle Tablecloth row ─────────────────────────────────────────────────
// Per Quoting_markdown/tablecloths.md's "Work Order Outputs" — Quantity,
// Size of table (length × width), Fabric. widthInches carries Length,
// heightInches carries Width.

interface TableclothRectangleRow {
  tempId: string;
  draftQuoteObjectId: number | null;
  roomWindow: string;
  quantity: string;
  size: string;
  fabric: string;
}

function objectToTableclothRectangleRow(obj: DraftQuoteObject): TableclothRectangleRow {
  return {
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: obj.id,
    roomWindow: obj.name || '',
    quantity: fmtNum(obj.quantity),
    size: obj.widthInches != null && obj.heightInches != null && obj.depthInches != null
      ? `${fmtNum(obj.widthInches)}" L × ${fmtNum(obj.heightInches)}" W × ${fmtNum(obj.depthInches)}" drop`
      : obj.widthInches != null && obj.heightInches != null
      ? `${fmtNum(obj.widthInches)}" L × ${fmtNum(obj.heightInches)}" W`
      : '',
    fabric: obj.fabricDescription || '',
  };
}

function tableclothRectangleRowToArgs(row: TableclothRectangleRow, index: number) {
  const notes = [
    row.size   && `Size: ${row.size}`,
    row.fabric && `Fabric: ${row.fabric}`,
  ].filter(Boolean).join(' | ');
  return {
    itemNumber: index + 1,
    description: row.roomWindow || `Tablecloth (Rectangle) ${index + 1}`,
    quantity: parseFloat(row.quantity) || 1,
    notes: notes || null,
    quoteLineItemId: null,
    draftQuoteObjectId: row.draftQuoteObjectId,
  };
}

// ── Furniture row ──────────────────────────────────────────────────────────────
// No shop SOP exists for furniture yet (see calcFurniture() in
// sopCalculations.ts) — this tab exists purely so the build spec collected
// on the draft object (DraftQuoteDetail.tsx's FurnitureForm) round-trips
// onto the traveler without being re-typed by hand, same "Work Order
// Outputs" convention as every other soft-goods type's own tab.

const FURNITURE_TYPE_LABELS: Record<string, string> = {
  loveseat: 'Loveseat', sofa_couch: 'Sofa / Couch', sectional: 'Sectional / L-Couch',
};

interface FurnitureRow {
  tempId: string;
  draftQuoteObjectId: number | null;
  roomWindow: string;
  quantity: string;
  type: string;
  size: string;
  seat: string;
  arms: string;
  back: string;
  legs: string;
  cushions: string;
  deck: string;
  fabric: string;
}

function objectToFurnitureRow(obj: DraftQuoteObject): FurnitureRow {
  return {
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: obj.id,
    roomWindow: obj.name || '',
    quantity: fmtNum(obj.quantity),
    type: obj.furnitureType ? (FURNITURE_TYPE_LABELS[obj.furnitureType] || obj.furnitureType) : '',
    size: obj.widthInches != null && obj.depthInches != null && obj.heightInches != null
      ? `${fmtNum(obj.widthInches)}" W × ${fmtNum(obj.depthInches)}" D × ${fmtNum(obj.heightInches)}" H`
      : '',
    seat: obj.seatHeightInches != null || obj.seatDepthInches != null
      ? `${fmtNum(obj.seatHeightInches)}" H × ${fmtNum(obj.seatDepthInches)}" D`
      : '',
    arms: obj.armHeightInches != null || obj.armThicknessInches != null
      ? `${fmtNum(obj.armHeightInches)}" H, ${fmtNum(obj.armThicknessInches)}" T`
      : '',
    back: obj.backHeightInches != null ? `${fmtNum(obj.backHeightInches)}" H` : '',
    legs: [obj.legHeightInches != null ? `${fmtNum(obj.legHeightInches)}" H` : null, obj.legProfile || null]
      .filter(Boolean).join(' — '),
    cushions: obj.cushionThicknessInches != null || obj.backCushionThicknessInches != null
      ? `Seat ${fmtNum(obj.cushionThicknessInches)}" T / Back ${fmtNum(obj.backCushionThicknessInches)}" T`
      : '',
    deck: obj.seatDeckThicknessInches != null ? `${fmtNum(obj.seatDeckThicknessInches)}" T` : '',
    fabric: obj.fabricDescription || '',
  };
}

function furnitureRowToArgs(row: FurnitureRow, index: number) {
  const notes = [
    row.type     && `Type: ${row.type}`,
    row.size     && `Size: ${row.size}`,
    row.seat     && `Seat: ${row.seat}`,
    row.arms     && `Arms: ${row.arms}`,
    row.back     && `Back: ${row.back}`,
    row.legs     && `Legs: ${row.legs}`,
    row.cushions && `Cushions: ${row.cushions}`,
    row.deck     && `Deck: ${row.deck}`,
    row.fabric   && `Fabric: ${row.fabric}`,
  ].filter(Boolean).join(' | ');
  return {
    itemNumber: index + 1,
    description: row.roomWindow || `Furniture ${index + 1}`,
    quantity: parseFloat(row.quantity) || 1,
    notes: notes || null,
    quoteLineItemId: null,
    draftQuoteObjectId: row.draftQuoteObjectId,
  };
}

// ── Bench row ──────────────────────────────────────────────────────────────────
// Per Quoting_markdown/benches.md — Bench has its own dedicated draft-object
// columns (bench_type/bench_shape/bench_style/storage_option/channel_size/
// pillow_top_size/leg_*/trim_*/nailhead_*), none of which the generic
// drapery traveler (rod face, overlaps, returns, etc.) has any business
// reading — dragging a bench object into the Drapery tab previously produced
// nonsense numbers (Rod Face/F.W./F.L. computed from bench's Length/Height
// misread as window width/floor-to-ceiling height). This dedicated tab reads
// bench's own fields directly instead.

const BENCH_TYPE_LABELS: Record<string, string> = {
  upholstered_to_floor: 'Upholstered to Floor', with_legs: 'Bench with Legs',
};
const BENCH_STYLE_LABELS: Record<string, string> = {
  standard: 'Standard', tufted: 'Tufted', channeled: 'Channeled', attached_pillow_top: 'Attached Pillow Top',
};

interface BenchRow {
  tempId: string;
  draftQuoteObjectId: number | null;
  roomWindow: string;
  quantity: string;
  family: string;
  shape: string;
  style: string;
  size: string;
  storage: string;
  legs: string;
  trim: string;
  nailheads: string;
  fabric: string;
}

function objectToBenchRow(obj: DraftQuoteObject): BenchRow {
  const trimParts = [obj.trimTop && 'Top', obj.trimBottom && 'Bottom'].filter(Boolean);
  const nailheadParts = [obj.nailheadTop && 'Top', obj.nailheadBottom && 'Bottom'].filter(Boolean);
  return {
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: obj.id,
    roomWindow: obj.name || '',
    quantity: fmtNum(obj.quantity),
    family: obj.benchType ? (BENCH_TYPE_LABELS[obj.benchType] || obj.benchType) : '',
    shape: obj.benchShape ? obj.benchShape.charAt(0).toUpperCase() + obj.benchShape.slice(1) : '',
    style: [
      obj.benchStyle ? (BENCH_STYLE_LABELS[obj.benchStyle] || obj.benchStyle) : null,
      obj.benchStyle === 'channeled' && obj.channelSize ? `(${obj.channelSize} channels)` : null,
      obj.benchStyle === 'attached_pillow_top' && obj.pillowTopSize ? `(Pillow Top: ${obj.pillowTopSize})` : null,
    ].filter(Boolean).join(' '),
    size: obj.widthInches != null && obj.depthInches != null
      ? `${fmtNum(obj.widthInches)}" W × ${fmtNum(obj.depthInches)}" D${obj.heightInches != null ? ` × ${fmtNum(obj.heightInches)}" H` : ''}`
      : '',
    storage: obj.storageOption ? 'Yes' : '',
    legs: obj.benchType === 'with_legs'
      ? [
          obj.legSize || obj.legStyle || obj.legFinish ? [obj.legSize, obj.legStyle, obj.legFinish].filter(Boolean).join(', ') : null,
          obj.legPriceCents != null ? `$${(obj.legPriceCents / 100).toFixed(2)} ea` : null,
        ].filter(Boolean).join(' — ')
      : '',
    trim: trimParts.length
      ? `${trimParts.join(' & ')}${obj.trimDescription ? ` (${obj.trimDescription})` : ''}`
      : '',
    nailheads: nailheadParts.length
      ? `${nailheadParts.join(' & ')}${obj.nailheadColor ? ` (${obj.nailheadColor})` : ''}`
      : '',
    fabric: obj.fabricDescription || '',
  };
}

function benchRowToArgs(row: BenchRow, index: number) {
  const notes = [
    row.family    && `Family: ${row.family}`,
    row.shape     && `Shape: ${row.shape}`,
    row.style     && `Style: ${row.style}`,
    row.size      && `Size: ${row.size}`,
    row.storage   && `Storage: ${row.storage}`,
    row.legs      && `Legs: ${row.legs}`,
    row.trim      && `Trim: ${row.trim}`,
    row.nailheads && `Nailheads: ${row.nailheads}`,
    row.fabric    && `Fabric: ${row.fabric}`,
  ].filter(Boolean).join(' | ');
  return {
    itemNumber: index + 1,
    description: row.roomWindow || `Bench ${index + 1}`,
    quantity: parseFloat(row.quantity) || 1,
    notes: notes || null,
    quoteLineItemId: null,
    draftQuoteObjectId: row.draftQuoteObjectId,
  };
}

// ── Cornice row ───────────────────────────────────────────────────────────────
// Per Quoting_markdown/cornices.md — the SOP's Work Order Outputs are width,
// height, returns, shape, and which of trim/self welt/nailheads apply.
// Width and Returns are derived here at traveler-build time (not stored on
// the draft object), same treatment as Traversing Drape's F.W./F.L., so they
// can't go stale if the mount type or window width is edited later:
//   Outside mount (standard): width = window + 2", returns 3.5" (5.5" when
//                             the cornice goes over another product)
//   Inside mount (rare):      width = window − ¼", no returns
// Keep in sync with corniceDimensions() in corniceCalculations.ts.

interface CorniceRow {
  tempId: string;
  draftQuoteObjectId: number | null;
  roomWindow: string;
  quantity: string;
  mount: string;
  width: string;
  height: string;
  returns: string;
  shape: string;
  trim: string;
  welt: string;
  nailheads: string;
  fabric: string;
}

// "Top, Bottom & Sides"
function corniceTreatmentSummary(p: { top: boolean | null; bottom: boolean | null; sides: boolean | null; face: boolean | null }, detail?: string | null): string {
  const names = [p.top && 'Top', p.bottom && 'Bottom', p.sides && 'Sides', p.face && 'Face'].filter(Boolean) as string[];
  if (!names.length) return '';
  const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}` : names[0];
  return detail ? `${list} (${detail})` : list;
}

function objectToCorniceRow(obj: DraftQuoteObject): CorniceRow {
  const inside = obj.mountType === 'inside';
  const windowWidth = obj.widthInches != null ? Number(obj.widthInches) : null;
  const corniceWidth = windowWidth != null ? (inside ? windowWidth - 0.25 : windowWidth + 2) : null;
  const returns = inside ? null : (obj.corniceOverProduct ? 5.5 : 3.5);
  return {
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: obj.id,
    roomWindow: obj.name || '',
    quantity: fmtNum(obj.quantity),
    mount: inside ? 'Inside Mount' : 'Outside Mount',
    width: corniceWidth != null ? `${fmtNum(corniceWidth)}"` : '',
    height: obj.heightInches != null ? `${fmtNum(obj.heightInches)}"` : '',
    returns: inside ? 'None' : `${fmtNum(returns)}"${obj.corniceOverProduct ? ' (over another product)' : ''}`,
    shape: obj.corniceShape === 'shaped' ? 'Shaped bottom' : obj.corniceShape === 'straight' ? 'Straight' : '',
    trim: corniceTreatmentSummary(
      { top: obj.trimTop, bottom: obj.trimBottom, sides: obj.trimSides, face: obj.trimFace }, obj.trimDescription),
    welt: corniceTreatmentSummary(
      { top: obj.weltTop, bottom: obj.weltBottom, sides: obj.weltSides, face: obj.weltFace }),
    nailheads: corniceTreatmentSummary(
      { top: obj.nailheadTop, bottom: obj.nailheadBottom, sides: obj.nailheadSides, face: obj.nailheadFace }, obj.nailheadColor),
    fabric: obj.fabricDescription || '',
  };
}

function corniceRowToArgs(row: CorniceRow, index: number) {
  const notes = [
    row.mount     && `Mount: ${row.mount}`,
    row.width     && `Width: ${row.width}`,
    row.height    && `Height: ${row.height}`,
    row.returns   && `Returns: ${row.returns}`,
    row.shape     && `Shape: ${row.shape}`,
    row.trim      && `Trim: ${row.trim}`,
    row.welt      && `Self Welt: ${row.welt}`,
    row.nailheads && `Nailheads: ${row.nailheads}`,
    row.fabric    && `Fabric: ${row.fabric}`,
  ].filter(Boolean).join(' | ');
  return {
    itemNumber: index + 1,
    description: row.roomWindow || `Cornice ${index + 1}`,
    quantity: parseFloat(row.quantity) || 1,
    notes: notes || null,
    quoteLineItemId: null,
    draftQuoteObjectId: row.draftQuoteObjectId,
  };
}

// ── Bed row ───────────────────────────────────────────────────────────────────
// Per Quoting_markdown/beds.md — Bed has its own dedicated draft-object
// columns (bed_config/headboard_style/headboard_shaped/bed_edge_treatment/
// trim_top/trim_sides/trim_face/mattress_*/frame_thickness_inches/
// side_rail_footboard_*/bed_drawers_*/bed_trundle/bed_feet_*/bed_bunky_*),
// none of which the generic drapery traveler has any business reading —
// same reasoning as Bench's own dedicated tab above.

const BED_CONFIG_LABELS: Record<string, string> = {
  headboard_only: 'Headboard Only', entire_bed: 'Entire Bed',
};
const HEADBOARD_STYLE_LABELS: Record<string, string> = {
  standard: 'Standard', tufted: 'Tufted', channeled: 'Channeled',
};
const BED_FEET_TYPE_LABELS: Record<string, string> = {
  wood: 'Wood', specialty: 'Specialty',
};

interface BedRow {
  tempId: string;
  draftQuoteObjectId: number | null;
  roomWindow: string;
  quantity: string;
  config: string;
  style: string;
  headboardSize: string;
  edgeTreatment: string;
  mattressSize: string;
  frameThickness: string;
  sideRailFootboardHeight: string;
  styleDescription: string;
  drawers: string;
  trundle: string;
  feet: string;
  bunkyBoards: string;
  fabric: string;
}

function objectToBedRow(obj: DraftQuoteObject): BedRow {
  const trimParts = [obj.trimTop && 'Top', obj.trimSides && 'Sides', obj.trimFace && 'Face'].filter(Boolean);
  const edgeTreatmentLabel = obj.bedEdgeTreatment === 'trim' ? 'Trim'
    : obj.bedEdgeTreatment === 'self_welt' ? 'Self Welt' : '';
  return {
    tempId: Math.random().toString(36).slice(2),
    draftQuoteObjectId: obj.id,
    roomWindow: obj.name || '',
    quantity: fmtNum(obj.quantity),
    config: obj.bedConfig ? (BED_CONFIG_LABELS[obj.bedConfig] || obj.bedConfig) : '',
    style: [
      obj.headboardStyle ? (HEADBOARD_STYLE_LABELS[obj.headboardStyle] || obj.headboardStyle) : null,
      obj.headboardShaped ? '(Shaped)' : null,
    ].filter(Boolean).join(' '),
    headboardSize: obj.widthInches != null && obj.heightInches != null
      ? `${fmtNum(obj.widthInches)}" W × ${fmtNum(obj.heightInches)}" H`
      : '',
    edgeTreatment: edgeTreatmentLabel && trimParts.length
      ? `${edgeTreatmentLabel} (${trimParts.join(', ')})`
      : edgeTreatmentLabel,
    mattressSize: obj.mattressLengthInches != null && obj.mattressWidthInches != null
      ? `${fmtNum(obj.mattressLengthInches)}" L × ${fmtNum(obj.mattressWidthInches)}" W`
      : '',
    frameThickness: obj.frameThicknessInches != null ? `${fmtNum(obj.frameThicknessInches)}"` : '',
    sideRailFootboardHeight: obj.sideRailFootboardHeightInches != null ? `${fmtNum(obj.sideRailFootboardHeightInches)}"` : '',
    styleDescription: obj.sideRailFootboardStyleDescription || '',
    drawers: [
      obj.bedDrawersSmallQty ? `${obj.bedDrawersSmallQty} ≤36"` : null,
      obj.bedDrawersLargeQty ? `${obj.bedDrawersLargeQty} >36"` : null,
    ].filter(Boolean).join(', '),
    trundle: obj.bedTrundle ? 'Yes' : '',
    feet: obj.bedFeetType && obj.bedFeetType !== 'none'
      ? [
          BED_FEET_TYPE_LABELS[obj.bedFeetType] || obj.bedFeetType,
          obj.bedFeetType === 'wood' ? [obj.bedFeetHeightInches != null ? `${fmtNum(obj.bedFeetHeightInches)}"` : null, obj.bedFeetFinish].filter(Boolean).join(', ') : null,
          obj.bedFeetType === 'specialty' ? obj.bedFeetSpecialtyDescription : null,
        ].filter(Boolean).join(' — ')
      : '',
    bunkyBoards: obj.bedBunkyBoards
      ? `Yes${obj.bedBunkyBoardWidthInches != null ? ` (${fmtNum(obj.bedBunkyBoardWidthInches)}" W)` : ''}`
      : '',
    fabric: obj.fabricDescription || '',
  };
}

function bedRowToArgs(row: BedRow, index: number) {
  const notes = [
    row.config                   && `Config: ${row.config}`,
    row.style                    && `Style: ${row.style}`,
    row.headboardSize            && `Headboard Size: ${row.headboardSize}`,
    row.edgeTreatment            && `Edge Treatment: ${row.edgeTreatment}`,
    row.mattressSize             && `Mattress Size: ${row.mattressSize}`,
    row.frameThickness           && `Frame Thickness: ${row.frameThickness}`,
    row.sideRailFootboardHeight  && `Side Rail/Footboard Height: ${row.sideRailFootboardHeight}`,
    row.styleDescription         && `Side Rail/Footboard Style: ${row.styleDescription}`,
    row.drawers                  && `Drawers: ${row.drawers}`,
    row.trundle                  && `Trundle: ${row.trundle}`,
    row.feet                     && `Feet: ${row.feet}`,
    row.bunkyBoards              && `Bunky Boards: ${row.bunkyBoards}`,
    row.fabric                   && `Fabric: ${row.fabric}`,
  ].filter(Boolean).join(' | ');
  return {
    itemNumber: index + 1,
    description: row.roomWindow || `Bed ${index + 1}`,
    quantity: parseFloat(row.quantity) || 1,
    notes: notes || null,
    quoteLineItemId: null,
    draftQuoteObjectId: row.draftQuoteObjectId,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function CreateWorkOrder({ quoteId, onClose, onComplete }: CreateWorkOrderProps) {
  const { user } = useAuth();

  const [form, setForm] = useState({ description: '', assignedTo: '', dueAt: '', notes: '', travelerNumber: '', designerClient: '' });
  const [workOrderType, setWorkOrderType] = useState<WorkOrderType>('drapery');
  const [travelerRows, setTravelerRows] = useState<TravelerRow[]>([]);
  const [rollerRows, setRollerRows] = useState<RollerRow[]>([]);
  const [shadeRows, setShadeRows] = useState<ShadeRow[]>([]);
  // Austrian Shade gets its own row list — split off from balloon_shade's
  // shadeRows/tab so it has a dedicated Work Order builder tab, even though
  // both reuse the identical ShadeRow shape/objectToShadeRow/shadeRowToArgs
  // (the fields are byte-identical per their markdown specs).
  const [austrianShadeRows, setAustrianShadeRows] = useState<ShadeRow[]>([]);
  const [pillowRows, setPillowRows] = useState<PillowRow[]>([]);
  const [shamRows, setShamRows] = useState<ShamRow[]>([]);
  const [bolsterRows, setBolsterRows] = useState<BolsterRow[]>([]);
  const [cushionRows, setCushionRows] = useState<CushionRow[]>([]);
  const [dustskirtRows, setDustskirtRows] = useState<DustskirtRow[]>([]);
  const [boxSpringRows, setBoxSpringRows] = useState<BoxSpringRow[]>([]);
  const [duvetRows, setDuvetRows] = useState<DuvetRow[]>([]);
  const [coverletRows, setCoverletRows] = useState<CoverletRow[]>([]);
  const [tableclothRoundRows, setTableclothRoundRows] = useState<TableclothRoundRow[]>([]);
  const [tableclothRectangleRows, setTableclothRectangleRows] = useState<TableclothRectangleRow[]>([]);
  const [furnitureRows, setFurnitureRows] = useState<FurnitureRow[]>([]);
  const [benchRows, setBenchRows] = useState<BenchRow[]>([]);
  const [corniceRows, setCorniceRows] = useState<CorniceRow[]>([]);
  const [bedRows, setBedRows] = useState<BedRow[]>([]);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [draggedObjectId, setDraggedObjectId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const hasAutoSetType = useRef(false);

  const { data: quoteData, loading: quoteLoading } = useQuery(QUOTE_BY_ID_QUERY, {
    variables: { id: quoteId },
    onCompleted: (data) => {
      const q = data?.quoteById;
      if (!q) return;

      // Auto-select the work order type from what the quote is actually made
      // of, instead of always defaulting to Drapery — only on first load, so
      // it doesn't stomp a manual tab switch on refetch.
      let initialType = workOrderType;
      if (!hasAutoSetType.current) {
        hasAutoSetType.current = true;
        const objects = (q.draftQuoteByDraftQuoteId?.draftQuoteObjectsByDraftQuoteId?.nodes || [])
          .filter((o: any) => !o.archivedAt);
        // PostGraphile serializes enum columns (item_type_enum) using their
        // GraphQL enum name, which is upper-cased ("ROLLER_SHADE"), not the
        // original lowercase Postgres value — normalize before comparing.
        const firstItemType = (objects[0]?.itemType as string | undefined)?.toLowerCase() ?? null;
        initialType = itemTypeToWO(firstItemType);
        setWorkOrderType(initialType);
      }

      if (q.name) {
        setForm((prev) => ({
          ...prev,
          description: `${q.name} - ${TYPE_LABELS[initialType]}`,
          designerClient: prev.designerClient || q.jobByJobId?.customerByCustomerId?.name || '',
        }));
      }
    },
  });

  const { data: usersData } = useQuery(ALL_USERS_QUERY, {
    variables: { domainId: user?.domain_id || 0 },
    skip: !user?.domain_id,
  });
  const [createWorkOrder] = useMutation(CREATE_WORK_ORDER_MUTATION);
  const [createWorkOrderLineItem] = useMutation(CREATE_WORK_ORDER_LINE_ITEM_MUTATION);
  const [createWorkOrderImage] = useMutation(CREATE_WORK_ORDER_IMAGE_MUTATION);
  const [selectedPhotoKeys, setSelectedPhotoKeys] = useState<Set<string>>(new Set());

  const quote = quoteData?.quoteById;
  const users: any[] = usersData?.allUsers?.nodes || [];

  // Normalize itemType once here — PostGraphile serializes item_type_enum
  // using its upper-cased GraphQL enum name ("ROLLER_SHADE"), not the
  // original lowercase Postgres value, so every downstream comparison
  // against 'roller_shade'/'roman_shade'/etc needs it lowercase.
  const draftObjects: DraftQuoteObject[] = (quote?.draftQuoteByDraftQuoteId?.draftQuoteObjectsByDraftQuoteId?.nodes || [])
    .filter((o: any) => !o.archivedAt)
    .map((o: any) => ({ ...o, itemType: o.itemType ? String(o.itemType).toLowerCase() : null }));

  // Every draft object on the quote is draggable regardless of its type —
  // dropping one auto-switches the active tab (see dropItem) instead of
  // requiring the user to pre-select the right tab first.
  const draggableObjects = draftObjects;

  const availablePhotos: GalleryPhoto[] = [
    ...draftObjects.flatMap((obj: any) =>
      (obj.draftQuoteObjectImagesByDraftQuoteObjectId?.nodes ?? [])
        .filter((img: any) => !img.archivedAt)
        .map((img: any): GalleryPhoto => ({
          key: `draft-${img.id}`,
          url: `${SERVER_URL}/uploads/object-images/${img.storedFilename}`,
          filename: img.originalFilename,
        })),
    ),
    ...(quote?.quoteImagesByQuoteId?.nodes || [])
      .filter((img: any) => !img.archivedAt)
      .map((img: any): GalleryPhoto => ({
        key: `quote-${img.id}`,
        url: `${SERVER_URL}/uploads/quote-images/${img.storedFilename}`,
        filename: img.originalFilename,
      })),
  ];

  const togglePhoto = (key: string) => {
    setSelectedPhotoKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // ── Drag helpers ───────────────────────────────────────────────────────────
  const dropItem = (insertAt?: number) => {
    if (draggedObjectId == null) return;
    const obj = draftObjects.find((o) => o.id === draggedObjectId);
    if (!obj) return;

    // Auto-fill the Work Order's own Notes field (not Description) from the
    // dragged object's shop notes (e.g. "with tailored front pleats and top
    // to remain pleated even when down") — appended, not replaced, so
    // dragging several objects in accumulates each one's note instead of
    // clobbering the previous drop's. Each line is prefixed with the
    // object's own Location/Label so multiple notes on one work order stay
    // attributable to the object they came from.
    if (obj.notes && obj.notes.trim()) {
      const line = `${obj.name || 'Object'} - ${obj.notes.trim()}`;
      setForm((prev) => ({
        ...prev,
        notes: prev.notes ? `${prev.notes}\n${line}` : line,
      }));
    }

    const newType = itemTypeToWO(obj.itemType);
    if (newType !== workOrderType) {
      setWorkOrderType(newType);
      setTravelerRows([]);
      setRollerRows([]);
      setShadeRows([]);
      setAustrianShadeRows([]);
      setPillowRows([]);
      setShamRows([]);
      setBolsterRows([]);
      setCushionRows([]);
      setDustskirtRows([]);
      setBoxSpringRows([]);
      setDuvetRows([]);
      setCoverletRows([]);
      setTableclothRoundRows([]);
      setTableclothRectangleRows([]);
      setFurnitureRows([]);
      setBenchRows([]);
      setCorniceRows([]);
      setBedRows([]);
    }

    if (newType === 'roller_shade') {
      setRollerRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, objectToRollerRow(obj));
        return next;
      });
    } else if (newType === 'balloon_shade') {
      setShadeRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, objectToShadeRow(obj));
        return next;
      });
    } else if (newType === 'austrian_shade') {
      setAustrianShadeRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, objectToShadeRow(obj));
        return next;
      });
    } else if (newType === 'pillow') {
      setPillowRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, objectToPillowRow(obj));
        return next;
      });
    } else if (newType === 'sham') {
      setShamRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, objectToShamRow(obj));
        return next;
      });
    } else if (newType === 'bolster_pillow') {
      setBolsterRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, objectToBolsterRow(obj));
        return next;
      });
    } else if (newType === 'cushion') {
      setCushionRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, objectToCushionRow(obj));
        return next;
      });
    } else if (newType === 'dustskirt') {
      setDustskirtRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, objectToDustskirtRow(obj));
        return next;
      });
    } else if (newType === 'box_spring_cover') {
      setBoxSpringRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, objectToBoxSpringRow(obj));
        return next;
      });
    } else if (newType === 'duvet') {
      setDuvetRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, objectToDuvetRow(obj));
        return next;
      });
    } else if (newType === 'coverlet') {
      setCoverletRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, objectToCoverletRow(obj));
        return next;
      });
    } else if (newType === 'tablecloth_round') {
      setTableclothRoundRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, objectToTableclothRoundRow(obj));
        return next;
      });
    } else if (newType === 'tablecloth_rectangle') {
      setTableclothRectangleRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, objectToTableclothRectangleRow(obj));
        return next;
      });
    } else if (newType === 'furniture') {
      setFurnitureRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, objectToFurnitureRow(obj));
        return next;
      });
    } else if (newType === 'bench') {
      setBenchRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, objectToBenchRow(obj));
        return next;
      });
    } else if (newType === 'cornice') {
      setCorniceRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, objectToCorniceRow(obj));
        return next;
      });
    } else if (newType === 'bed') {
      setBedRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, objectToBedRow(obj));
        return next;
      });
    } else {
      setTravelerRows((prev) => {
        const next = [...prev];
        next.splice(insertAt ?? prev.length, 0, ...objectToTravelerRows(obj, newType));
        return next;
      });
    }
    setDraggedObjectId(null);
    setDragOverIndex(null);
  };

  // ── Traveler row helpers ───────────────────────────────────────────────────
  const updateTraveler = (i: number, field: TravelerTextField, val: string) =>
    setTravelerRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeTraveler = (i: number) => setTravelerRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Roller row helpers ─────────────────────────────────────────────────────
  const updateRoller = (i: number, field: RollerTextField, val: string) =>
    setRollerRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeRoller = (i: number) => setRollerRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Shade row helpers ──────────────────────────────────────────────────────
  const updateShade = (i: number, field: ShadeTextField, val: string) =>
    setShadeRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeShade = (i: number) => setShadeRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Austrian Shade row helpers ─────────────────────────────────────────────
  const updateAustrianShade = (i: number, field: ShadeTextField, val: string) =>
    setAustrianShadeRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeAustrianShade = (i: number) => setAustrianShadeRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Pillow row helpers ─────────────────────────────────────────────────────
  const updatePillow = (i: number, field: PillowTextField, val: string) =>
    setPillowRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removePillow = (i: number) => setPillowRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Sham row helpers ───────────────────────────────────────────────────────
  const updateSham = (i: number, field: ShamTextField, val: string) =>
    setShamRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeSham = (i: number) => setShamRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Bolster row helpers ────────────────────────────────────────────────────
  const updateBolster = (i: number, field: BolsterTextField, val: string) =>
    setBolsterRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeBolster = (i: number) => setBolsterRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Cushion row helpers ────────────────────────────────────────────────────
  const updateCushion = (i: number, field: CushionTextField, val: string) =>
    setCushionRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeCushion = (i: number) => setCushionRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Dustskirt row helpers ──────────────────────────────────────────────────
  const updateDustskirt = (i: number, field: DustskirtTextField, val: string) =>
    setDustskirtRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeDustskirt = (i: number) => setDustskirtRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Box Spring Cover row helpers ───────────────────────────────────────────
  const updateBoxSpring = (i: number, field: BoxSpringTextField, val: string) =>
    setBoxSpringRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeBoxSpring = (i: number) => setBoxSpringRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Duvet row helpers ──────────────────────────────────────────────────────
  const updateDuvet = (i: number, field: DuvetTextField, val: string) =>
    setDuvetRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeDuvet = (i: number) => setDuvetRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Coverlet row helpers ───────────────────────────────────────────────────
  const updateCoverlet = (i: number, field: CoverletTextField, val: string) =>
    setCoverletRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeCoverlet = (i: number) => setCoverletRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Round Tablecloth row helpers ───────────────────────────────────────────
  const updateTableclothRound = (i: number, field: TableclothRoundTextField, val: string) =>
    setTableclothRoundRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeTableclothRound = (i: number) => setTableclothRoundRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Rectangle Tablecloth row helpers ───────────────────────────────────────
  const updateTableclothRectangle = (i: number, field: TableclothRectangleTextField, val: string) =>
    setTableclothRectangleRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeTableclothRectangle = (i: number) => setTableclothRectangleRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Furniture row helpers ──────────────────────────────────────────────────
  const updateFurniture = (i: number, field: FurnitureTextField, val: string) =>
    setFurnitureRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeFurniture = (i: number) => setFurnitureRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Bench row helpers ──────────────────────────────────────────────────────
  const updateBench = (i: number, field: BenchTextField, val: string) =>
    setBenchRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeBench = (i: number) => setBenchRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Cornice row helpers ────────────────────────────────────────────────────
  const updateCornice = (i: number, field: CorniceTextField, val: string) =>
    setCorniceRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeCornice = (i: number) => setCorniceRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Bed row helpers ────────────────────────────────────────────────────────
  const updateBed = (i: number, field: BedTextField, val: string) =>
    setBedRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeBed = (i: number) => setBedRows((prev) => prev.filter((_, idx) => idx !== i));

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!form.description.trim()) { setError('Work order description is required'); return; }
    if (!user?.id) { setError('Not authenticated'); return; }
    setSaving(true);
    setError(null);
    try {
      const woResult = await createWorkOrder({
        variables: {
          domainId: user.domain_id,
          quoteId,
          description: form.description,
          status: 'ready',
          createdBy: user.id,
          assignedTo: form.assignedTo ? parseInt(form.assignedTo) : null,
          dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : null,
          notes: form.notes || null,
          travelerNumber: form.travelerNumber || null,
          type: workOrderType,
          designerClient: form.designerClient || null,
        },
      });
      const workOrderId = woResult.data?.createWorkOrder?.workOrder?.id;
      if (!workOrderId) throw new Error('Work order creation returned no ID');

      const lineArgs = workOrderType === 'roller_shade'
        ? rollerRows.map(rollerRowToArgs)
        : workOrderType === 'balloon_shade'
        ? shadeRows.map(shadeRowToArgs)
        : workOrderType === 'austrian_shade'
        ? austrianShadeRows.map(shadeRowToArgs)
        : workOrderType === 'pillow'
        ? pillowRows.map(pillowRowToArgs)
        : workOrderType === 'sham'
        ? shamRows.map(shamRowToArgs)
        : workOrderType === 'bolster_pillow'
        ? bolsterRows.map(bolsterRowToArgs)
        : workOrderType === 'cushion'
        ? cushionRows.map(cushionRowToArgs)
        : workOrderType === 'dustskirt'
        ? dustskirtRows.map(dustskirtRowToArgs)
        : workOrderType === 'box_spring_cover'
        ? boxSpringRows.map(boxSpringRowToArgs)
        : workOrderType === 'duvet'
        ? duvetRows.map(duvetRowToArgs)
        : workOrderType === 'coverlet'
        ? coverletRows.map(coverletRowToArgs)
        : workOrderType === 'tablecloth_round'
        ? tableclothRoundRows.map(tableclothRoundRowToArgs)
        : workOrderType === 'tablecloth_rectangle'
        ? tableclothRectangleRows.map(tableclothRectangleRowToArgs)
        : workOrderType === 'furniture'
        ? furnitureRows.map(furnitureRowToArgs)
        : workOrderType === 'bench'
        ? benchRows.map(benchRowToArgs)
        : workOrderType === 'cornice'
        ? corniceRows.map(corniceRowToArgs)
        : workOrderType === 'bed'
        ? bedRows.map(bedRowToArgs)
        : travelerRows.map(travelerRowToArgs);

      for (const args of lineArgs) {
        await createWorkOrderLineItem({ variables: { workOrderId, ...args } });
      }

      for (const key of Array.from(selectedPhotoKeys)) {
        const [source, imageId] = key.split('-');
        await createWorkOrderImage({
          variables: {
            workOrderId,
            draftQuoteObjectImageId: source === 'draft' ? parseInt(imageId, 10) : null,
            quoteImageId: source === 'quote' ? parseInt(imageId, 10) : null,
          },
        });
      }

      onComplete?.(workOrderId);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create work order');
    } finally {
      setSaving(false);
    }
  };

  const todayStr = new Date().toLocaleDateString();

  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        {/* ── Header ── */}
        <div style={s.modalHeader}>
          <h2 style={s.modalTitle}>New Work Order</h2>
          <button style={s.closeBtn} onClick={onClose} title="Close"><CloseIcon fontSize="small" /></button>
        </div>

        <div style={s.body}>
          {/* ── Traveler header ── */}
          <div style={s.travelerHeader}>
            <div style={s.hCell}>
              <span style={s.hLabel}>Assigned to:</span>
              <select style={s.hInput} value={form.assignedTo} onChange={(e) => setForm({ ...form, assignedTo: e.target.value })} disabled={saving}>
                <option value="">— select —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.username}</option>)}
              </select>
            </div>
            <div style={{ ...s.hCell, flex: 2 }}>
              <span style={s.hLabel}>Designer/Client:</span>
              <input style={s.hInput} value={form.designerClient} onChange={(e) => setForm({ ...form, designerClient: e.target.value })} disabled={saving} placeholder="Customer name" />
            </div>
            <div style={s.hCell}>
              <span style={s.hLabel}>Date:</span>
              <span style={s.hValue}>{todayStr}</span>
            </div>
            <div style={{ ...s.hCell, borderRight: 'none' }}>
              <span style={s.hLabel}>Install Date:</span>
              <input type="date" style={s.hInput} value={form.dueAt} onChange={(e) => setForm({ ...form, dueAt: e.target.value })} disabled={saving} />
            </div>
          </div>

          {/* ── Description + traveler # ── */}
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 2 }}>
              <label style={s.label}>Work Order Description <span style={{ color: '#dc2626' }}>*</span></label>
              <input type="text" style={s.input} placeholder="e.g., Master Bedroom — roman shades" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} disabled={saving} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={s.label}>Traveler #</label>
              <input type="text" style={s.input} placeholder="e.g., T-001" value={form.travelerNumber} onChange={(e) => setForm({ ...form, travelerNumber: e.target.value })} disabled={saving} />
            </div>
          </div>

          {/* ── Photo picker ── */}
          {availablePhotos.length > 0 && (
            <div>
              <label style={s.label}>Photos {selectedPhotoKeys.size > 0 && `(${selectedPhotoKeys.size} selected)`}</label>
              <PhotoGallery photos={availablePhotos} selectable selectedKeys={selectedPhotoKeys} onToggle={togglePhoto} />
            </div>
          )}

          {/* ── Two-panel layout ── */}
          <div style={s.panels}>
            {/* Left: every draft object on the quote, regardless of type */}
            <div style={s.leftPanel}>
              <div style={s.panelTitle}>Quote Objects</div>
              {quoteLoading && <div style={s.hint}>Loading…</div>}
              {!quoteLoading && draggableObjects.length === 0 && (
                <div style={s.hint}>No objects on this quote.</div>
              )}
              {draggableObjects.map((obj) => (
                <div
                  key={obj.id}
                  draggable
                  onDragStart={() => setDraggedObjectId(obj.id)}
                  style={{ ...s.draggableItem, backgroundColor: '#dbeafe', opacity: draggedObjectId === obj.id ? 0.4 : 1 }}
                >
                  <span style={s.dragHandle}><DragIndicatorIcon sx={{ fontSize: 15, display: 'block' }} /></span>
                  <div>
                    <div style={s.itemDesc}>{obj.name || '(unnamed)'}</div>
                    <div style={s.itemMeta}>
                      {fmtNum(obj.widthInches)}" × {fmtNum(obj.heightInches)}"
                      {obj.mountType ? ` · ${obj.mountType === 'inside' ? 'IM' : 'OM'}` : ''}
                    </div>
                    {obj.fabricDescription && (
                      <div style={{ ...s.itemMeta, fontStyle: 'italic' }}>{obj.fabricDescription}</div>
                    )}
                  </div>
                </div>
              ))}
              <div style={{ ...s.hint, display: 'flex', alignItems: 'center', gap: 4 }}>
                Drag to add <ArrowForwardIcon sx={{ fontSize: 14 }} />
              </div>
            </div>

            {/* Right: drop zone — switches based on type */}
            <div style={s.rightPanel}>
              <div style={s.panelTitle}>{TYPE_LABELS[workOrderType]} Line Items</div>

              {workOrderType === 'roller_shade' ? (
                <RollerDropZone
                  rows={rollerRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updateRoller}
                  onRemove={removeRoller}
                  onAddEmpty={() => setRollerRows((prev) => [...prev, emptyRollerRow()])}
                />
              ) : workOrderType === 'balloon_shade' ? (
                <ShadeDropZone
                  rows={shadeRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updateShade}
                  onRemove={removeShade}
                />
              ) : workOrderType === 'austrian_shade' ? (
                <ShadeDropZone
                  rows={austrianShadeRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updateAustrianShade}
                  onRemove={removeAustrianShade}
                />
              ) : workOrderType === 'pillow' ? (
                <PillowDropZone
                  rows={pillowRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updatePillow}
                  onRemove={removePillow}
                />
              ) : workOrderType === 'sham' ? (
                <ShamDropZone
                  rows={shamRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updateSham}
                  onRemove={removeSham}
                />
              ) : workOrderType === 'bolster_pillow' ? (
                <BolsterDropZone
                  rows={bolsterRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updateBolster}
                  onRemove={removeBolster}
                />
              ) : workOrderType === 'cushion' ? (
                <CushionDropZone
                  rows={cushionRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updateCushion}
                  onRemove={removeCushion}
                />
              ) : workOrderType === 'dustskirt' ? (
                <DustskirtDropZone
                  rows={dustskirtRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updateDustskirt}
                  onRemove={removeDustskirt}
                />
              ) : workOrderType === 'box_spring_cover' ? (
                <BoxSpringDropZone
                  rows={boxSpringRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updateBoxSpring}
                  onRemove={removeBoxSpring}
                />
              ) : workOrderType === 'duvet' ? (
                <DuvetDropZone
                  rows={duvetRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updateDuvet}
                  onRemove={removeDuvet}
                />
              ) : workOrderType === 'coverlet' ? (
                <CoverletDropZone
                  rows={coverletRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updateCoverlet}
                  onRemove={removeCoverlet}
                />
              ) : workOrderType === 'tablecloth_round' ? (
                <TableclothRoundDropZone
                  rows={tableclothRoundRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updateTableclothRound}
                  onRemove={removeTableclothRound}
                />
              ) : workOrderType === 'tablecloth_rectangle' ? (
                <TableclothRectangleDropZone
                  rows={tableclothRectangleRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updateTableclothRectangle}
                  onRemove={removeTableclothRectangle}
                />
              ) : workOrderType === 'furniture' ? (
                <FurnitureDropZone
                  rows={furnitureRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updateFurniture}
                  onRemove={removeFurniture}
                />
              ) : workOrderType === 'bench' ? (
                <BenchDropZone
                  rows={benchRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updateBench}
                  onRemove={removeBench}
                />
              ) : workOrderType === 'cornice' ? (
                <CorniceDropZone
                  rows={corniceRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updateCornice}
                  onRemove={removeCornice}
                />
              ) : workOrderType === 'bed' ? (
                <BedDropZone
                  rows={bedRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updateBed}
                  onRemove={removeBed}
                />
              ) : (
                <TravelerDropZone
                  rows={travelerRows}
                  dragOverIndex={dragOverIndex}
                  saving={saving}
                  onDragOver={(idx) => setDragOverIndex(idx)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={(idx) => dropItem(idx)}
                  onUpdate={updateTraveler}
                  onRemove={removeTraveler}
                />
              )}

              <div style={{ marginTop: 12 }}>
                <label style={s.label}>Work Order Notes</label>
                <textarea style={s.textarea} placeholder="Special instructions, notes, etc." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={saving} />
              </div>
            </div>
          </div>

          {error && <div style={s.errorBox}>{error}</div>}
        </div>

        <div style={s.footer}>
          <button style={s.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={s.saveBtn} onClick={handleSubmit} disabled={saving}>{saving ? 'Creating…' : 'Create Work Order'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Traveler drop zone (drapery + roman shade) ────────────────────────────────

type TravelerTextField = Exclude<keyof TravelerRow, 'tempId' | 'draftQuoteObjectId'>;

const TRAVELER_FIELDS: [TravelerTextField, number, string][] = [
  ['fabricColor', 120, 'Fabric/Color'],
  ['yds',         46,  'Yds'],
  ['qty',         44,  'Qty'],
  ['numPairs',    46,  '# Prs'],
  ['widthPanel',  60,  'Width/Panel'],
  ['rodFace',     60,  'Rod Face'],
  ['fw',          50,  'F.W.'],
  ['fl',          50,  'F.L.'],
  ['hemSize',     60,  'Hem Size'],
  ['returns',     52,  'Returns'],
  ['overlaps',    52,  'Overlaps'],
  ['line',        44,  'Line'],
  ['interLine',   60,  'Inter-Line'],
  ['roomWindow',  80,  'Room/Window'],
  ['hardware',    70,  'Hardware'],
  ['motor',       70,  'Operating System'],
  ['pleatType',   80,  'Pleat Type'],
  ['board',       90,  'Board Spec'],
  ['flap',        50,  'Flap'],
];

function TravelerDropZone({ rows, dragOverIndex, saving, onDragOver, onDragLeave, onDrop, onUpdate, onRemove }: {
  rows: TravelerRow[];
  dragOverIndex: number | null;
  saving: boolean;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number) => void;
  onUpdate: (i: number, field: TravelerTextField, val: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div
      style={{ ...s.dropZone, borderColor: dragOverIndex === -1 ? '#6366f1' : '#d1d5db', backgroundColor: dragOverIndex === -1 ? '#eef2ff' : '#fafafa' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(-1); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
    >
      {rows.length === 0 ? (
        <div style={s.emptyDrop}>Drop objects here</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead>
              <tr>
                {TRAVELER_FIELDS.map(([, , label]) => <th key={label} style={s.th}>{label}</th>)}
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.tempId}
                  style={{ backgroundColor: dragOverIndex === i ? '#eef2ff' : undefined }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(i); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(i); }}
                >
                  {TRAVELER_FIELDS.map(([field, w]) => (
                    <td key={field} style={s.td}>
                      <input type="text" value={row[field]} onChange={(e) => onUpdate(i, field, e.target.value)} style={{ ...s.cellInput, minWidth: w, width: '100%' }} disabled={saving} />
                    </td>
                  ))}
                  <td style={s.td}>
                    <button onClick={() => onRemove(i)} style={s.removeBtn} disabled={saving} title="Remove"><CloseIcon sx={{ fontSize: 14 }} /></button>
                  </td>
                </tr>
              ))}
              <tr
                style={{ height: 28, backgroundColor: dragOverIndex === rows.length ? '#eef2ff' : undefined }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(rows.length); }}
                onDragLeave={onDragLeave}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(rows.length); }}
              >
                <td colSpan={TRAVELER_FIELDS.length + 1} style={{ ...s.td, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  + drop to add another row
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Austrian / Balloon shade drop zone ───────────────────────────────────────

type ShadeTextField = Exclude<keyof ShadeRow, 'tempId' | 'draftQuoteObjectId'>;

const SHADE_FIELDS: [ShadeTextField, number, string][] = [
  ['roomWindow',   90,  'Room/Window'],
  ['width',        50,  'Width'],
  ['length',       50,  'Length'],
  ['mountType',    44,  'Mount'],
  ['fabric',       110, 'Fabric/Color'],
  ['lining',       70,  'Lining'],
  ['interlining',  70,  'Interlining'],
  ['trim',         40,  'Trim'],
  ['motor',        90,  'Operating System'],
  ['board',        110, 'Board Spec'],
  ['returns',      55,  'Returns'],
];

function ShadeDropZone({ rows, dragOverIndex, saving, onDragOver, onDragLeave, onDrop, onUpdate, onRemove }: {
  rows: ShadeRow[];
  dragOverIndex: number | null;
  saving: boolean;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number) => void;
  onUpdate: (i: number, field: ShadeTextField, val: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div
      style={{ ...s.dropZone, borderColor: dragOverIndex === -1 ? '#6366f1' : '#d1d5db', backgroundColor: dragOverIndex === -1 ? '#eef2ff' : '#fafafa' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(-1); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
    >
      {rows.length === 0 ? (
        <div style={s.emptyDrop}>Drop shade objects here</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead>
              <tr>
                {SHADE_FIELDS.map(([, , label]) => <th key={label} style={s.th}>{label}</th>)}
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.tempId}
                  style={{ backgroundColor: dragOverIndex === i ? '#eef2ff' : undefined }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(i); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(i); }}
                >
                  {SHADE_FIELDS.map(([field, w]) => (
                    <td key={field} style={s.td}>
                      <input type="text" value={row[field]} onChange={(e) => onUpdate(i, field, e.target.value)} style={{ ...s.cellInput, minWidth: w, width: '100%' }} disabled={saving} />
                    </td>
                  ))}
                  <td style={s.td}>
                    <button onClick={() => onRemove(i)} style={s.removeBtn} disabled={saving} title="Remove"><CloseIcon sx={{ fontSize: 14 }} /></button>
                  </td>
                </tr>
              ))}
              <tr
                style={{ height: 28, backgroundColor: dragOverIndex === rows.length ? '#eef2ff' : undefined }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(rows.length); }}
                onDragLeave={onDragLeave}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(rows.length); }}
              >
                <td colSpan={SHADE_FIELDS.length + 1} style={{ ...s.td, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  + drop to add another row
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Pillow drop zone ──────────────────────────────────────────────────────────

type PillowTextField = Exclude<keyof PillowRow, 'tempId' | 'draftQuoteObjectId'>;

const PILLOW_FIELDS: [PillowTextField, number, string][] = [
  ['roomWindow',      100, 'Room/Window'],
  ['quantity',        60,  'Quantity'],
  ['size',            90,  'Size'],
  ['zipper',          60,  'Zipper'],
  ['insert',           110, 'Insert'],
  ['fabric',          140, 'Fabric'],
  ['weltTrimRuffle',  140, 'Welt/Trim/Ruffle'],
  ['contrastWeltFabric', 140, 'Contrast Welt Fabric'],
  ['trimName',        140, 'Trim Name'],
];

function PillowDropZone({ rows, dragOverIndex, saving, onDragOver, onDragLeave, onDrop, onUpdate, onRemove }: {
  rows: PillowRow[];
  dragOverIndex: number | null;
  saving: boolean;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number) => void;
  onUpdate: (i: number, field: PillowTextField, val: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div
      style={{ ...s.dropZone, borderColor: dragOverIndex === -1 ? '#6366f1' : '#d1d5db', backgroundColor: dragOverIndex === -1 ? '#eef2ff' : '#fafafa' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(-1); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
    >
      {rows.length === 0 ? (
        <div style={s.emptyDrop}>Drop pillow objects here</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead>
              <tr>
                {PILLOW_FIELDS.map(([, , label]) => <th key={label} style={s.th}>{label}</th>)}
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.tempId}
                  style={{ backgroundColor: dragOverIndex === i ? '#eef2ff' : undefined }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(i); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(i); }}
                >
                  {PILLOW_FIELDS.map(([field, w]) => (
                    <td key={field} style={s.td}>
                      <input type="text" value={row[field]} onChange={(e) => onUpdate(i, field, e.target.value)} style={{ ...s.cellInput, minWidth: w, width: '100%' }} disabled={saving} />
                    </td>
                  ))}
                  <td style={s.td}>
                    <button onClick={() => onRemove(i)} style={s.removeBtn} disabled={saving} title="Remove"><CloseIcon sx={{ fontSize: 14 }} /></button>
                  </td>
                </tr>
              ))}
              <tr
                style={{ height: 28, backgroundColor: dragOverIndex === rows.length ? '#eef2ff' : undefined }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(rows.length); }}
                onDragLeave={onDragLeave}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(rows.length); }}
              >
                <td colSpan={PILLOW_FIELDS.length + 1} style={{ ...s.td, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  + drop to add another row
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Sham drop zone ────────────────────────────────────────────────────────────

type ShamTextField = Exclude<keyof ShamRow, 'tempId' | 'draftQuoteObjectId'>;

const SHAM_FIELDS: [ShamTextField, number, string][] = [
  ['roomWindow',      100, 'Room/Window'],
  ['quantity',        60,  'Quantity'],
  ['size',            90,  'Size'],
  ['zipper',          60,  'Zipper'],
  ['insert',           110, 'Insert'],
  ['fabric',          140, 'Fabric'],
  ['weltTrimRuffle',  140, 'Welt/Trim/Ruffle'],
  ['contrastWeltFabric', 140, 'Contrast Welt Fabric'],
  ['trimName',        140, 'Trim Name'],
];

function ShamDropZone({ rows, dragOverIndex, saving, onDragOver, onDragLeave, onDrop, onUpdate, onRemove }: {
  rows: ShamRow[];
  dragOverIndex: number | null;
  saving: boolean;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number) => void;
  onUpdate: (i: number, field: ShamTextField, val: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div
      style={{ ...s.dropZone, borderColor: dragOverIndex === -1 ? '#6366f1' : '#d1d5db', backgroundColor: dragOverIndex === -1 ? '#eef2ff' : '#fafafa' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(-1); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
    >
      {rows.length === 0 ? (
        <div style={s.emptyDrop}>Drop sham objects here</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead>
              <tr>
                {SHAM_FIELDS.map(([, , label]) => <th key={label} style={s.th}>{label}</th>)}
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.tempId}
                  style={{ backgroundColor: dragOverIndex === i ? '#eef2ff' : undefined }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(i); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(i); }}
                >
                  {SHAM_FIELDS.map(([field, w]) => (
                    <td key={field} style={s.td}>
                      <input type="text" value={row[field]} onChange={(e) => onUpdate(i, field, e.target.value)} style={{ ...s.cellInput, minWidth: w, width: '100%' }} disabled={saving} />
                    </td>
                  ))}
                  <td style={s.td}>
                    <button onClick={() => onRemove(i)} style={s.removeBtn} disabled={saving} title="Remove"><CloseIcon sx={{ fontSize: 14 }} /></button>
                  </td>
                </tr>
              ))}
              <tr
                style={{ height: 28, backgroundColor: dragOverIndex === rows.length ? '#eef2ff' : undefined }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(rows.length); }}
                onDragLeave={onDragLeave}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(rows.length); }}
              >
                <td colSpan={SHAM_FIELDS.length + 1} style={{ ...s.td, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  + drop to add another row
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Bolster Pillow drop zone ──────────────────────────────────────────────────

type BolsterTextField = Exclude<keyof BolsterRow, 'tempId' | 'draftQuoteObjectId'>;

const BOLSTER_FIELDS: [BolsterTextField, number, string][] = [
  ['roomWindow',      100, 'Room/Window'],
  ['quantity',        60,  'Quantity'],
  ['size',            110, 'Size'],
  ['zipper',          60,  'Zipper'],
  ['insert',           80, 'Insert'],
  ['fabric',          140, 'Fabric'],
  ['weltTrimRuffle',  140, 'Welt/Trim/Ruffle'],
  ['contrastWeltFabric', 140, 'Contrast Welt Fabric'],
  ['trimName',        140, 'Trim Name'],
];

function BolsterDropZone({ rows, dragOverIndex, saving, onDragOver, onDragLeave, onDrop, onUpdate, onRemove }: {
  rows: BolsterRow[];
  dragOverIndex: number | null;
  saving: boolean;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number) => void;
  onUpdate: (i: number, field: BolsterTextField, val: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div
      style={{ ...s.dropZone, borderColor: dragOverIndex === -1 ? '#6366f1' : '#d1d5db', backgroundColor: dragOverIndex === -1 ? '#eef2ff' : '#fafafa' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(-1); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
    >
      {rows.length === 0 ? (
        <div style={s.emptyDrop}>Drop bolster pillow objects here</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead>
              <tr>
                {BOLSTER_FIELDS.map(([, , label]) => <th key={label} style={s.th}>{label}</th>)}
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.tempId}
                  style={{ backgroundColor: dragOverIndex === i ? '#eef2ff' : undefined }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(i); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(i); }}
                >
                  {BOLSTER_FIELDS.map(([field, w]) => (
                    <td key={field} style={s.td}>
                      <input type="text" value={row[field]} onChange={(e) => onUpdate(i, field, e.target.value)} style={{ ...s.cellInput, minWidth: w, width: '100%' }} disabled={saving} />
                    </td>
                  ))}
                  <td style={s.td}>
                    <button onClick={() => onRemove(i)} style={s.removeBtn} disabled={saving} title="Remove"><CloseIcon sx={{ fontSize: 14 }} /></button>
                  </td>
                </tr>
              ))}
              <tr
                style={{ height: 28, backgroundColor: dragOverIndex === rows.length ? '#eef2ff' : undefined }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(rows.length); }}
                onDragLeave={onDragLeave}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(rows.length); }}
              >
                <td colSpan={BOLSTER_FIELDS.length + 1} style={{ ...s.td, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  + drop to add another row
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Cushion drop zone ──────────────────────────────────────────────────────────

type CushionTextField = Exclude<keyof CushionRow, 'tempId' | 'draftQuoteObjectId'>;

const CUSHION_FIELDS: [CushionTextField, number, string][] = [
  ['roomWindow', 100, 'Room/Window'],
  ['quantity',   60,  'Quantity'],
  ['size',       140, 'Size'],
  ['zipper',     60,  'Zipper'],
  ['insert',     80,  'Insert'],
  ['fabric',     140, 'Fabric'],
  ['weltTrim',   120, 'Welt/Trim'],
];

function CushionDropZone({ rows, dragOverIndex, saving, onDragOver, onDragLeave, onDrop, onUpdate, onRemove }: {
  rows: CushionRow[];
  dragOverIndex: number | null;
  saving: boolean;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number) => void;
  onUpdate: (i: number, field: CushionTextField, val: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div
      style={{ ...s.dropZone, borderColor: dragOverIndex === -1 ? '#6366f1' : '#d1d5db', backgroundColor: dragOverIndex === -1 ? '#eef2ff' : '#fafafa' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(-1); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
    >
      {rows.length === 0 ? (
        <div style={s.emptyDrop}>Drop cushion objects here</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead>
              <tr>
                {CUSHION_FIELDS.map(([, , label]) => <th key={label} style={s.th}>{label}</th>)}
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.tempId}
                  style={{ backgroundColor: dragOverIndex === i ? '#eef2ff' : undefined }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(i); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(i); }}
                >
                  {CUSHION_FIELDS.map(([field, w]) => (
                    <td key={field} style={s.td}>
                      <input type="text" value={row[field]} onChange={(e) => onUpdate(i, field, e.target.value)} style={{ ...s.cellInput, minWidth: w, width: '100%' }} disabled={saving} />
                    </td>
                  ))}
                  <td style={s.td}>
                    <button onClick={() => onRemove(i)} style={s.removeBtn} disabled={saving} title="Remove"><CloseIcon sx={{ fontSize: 14 }} /></button>
                  </td>
                </tr>
              ))}
              <tr
                style={{ height: 28, backgroundColor: dragOverIndex === rows.length ? '#eef2ff' : undefined }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(rows.length); }}
                onDragLeave={onDragLeave}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(rows.length); }}
              >
                <td colSpan={CUSHION_FIELDS.length + 1} style={{ ...s.td, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  + drop to add another row
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Dustskirt drop zone ────────────────────────────────────────────────────────

type DustskirtTextField = Exclude<keyof DustskirtRow, 'tempId' | 'draftQuoteObjectId'>;

const DUSTSKIRT_FIELDS: [DustskirtTextField, number, string][] = [
  ['roomWindow', 100, 'Room/Window'],
  ['quantity',   60,  'Quantity'],
  ['bedSize',    90,  'Bed Size'],
  ['style',      100, 'Style'],
  ['fabric',     140, 'Fabric'],
  ['drop',       80,  'Drop'],
];

function DustskirtDropZone({ rows, dragOverIndex, saving, onDragOver, onDragLeave, onDrop, onUpdate, onRemove }: {
  rows: DustskirtRow[];
  dragOverIndex: number | null;
  saving: boolean;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number) => void;
  onUpdate: (i: number, field: DustskirtTextField, val: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div
      style={{ ...s.dropZone, borderColor: dragOverIndex === -1 ? '#6366f1' : '#d1d5db', backgroundColor: dragOverIndex === -1 ? '#eef2ff' : '#fafafa' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(-1); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
    >
      {rows.length === 0 ? (
        <div style={s.emptyDrop}>Drop dustskirt objects here</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead>
              <tr>
                {DUSTSKIRT_FIELDS.map(([, , label]) => <th key={label} style={s.th}>{label}</th>)}
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.tempId}
                  style={{ backgroundColor: dragOverIndex === i ? '#eef2ff' : undefined }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(i); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(i); }}
                >
                  {DUSTSKIRT_FIELDS.map(([field, w]) => (
                    <td key={field} style={s.td}>
                      <input type="text" value={row[field]} onChange={(e) => onUpdate(i, field, e.target.value)} style={{ ...s.cellInput, minWidth: w, width: '100%' }} disabled={saving} />
                    </td>
                  ))}
                  <td style={s.td}>
                    <button onClick={() => onRemove(i)} style={s.removeBtn} disabled={saving} title="Remove"><CloseIcon sx={{ fontSize: 14 }} /></button>
                  </td>
                </tr>
              ))}
              <tr
                style={{ height: 28, backgroundColor: dragOverIndex === rows.length ? '#eef2ff' : undefined }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(rows.length); }}
                onDragLeave={onDragLeave}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(rows.length); }}
              >
                <td colSpan={DUSTSKIRT_FIELDS.length + 1} style={{ ...s.td, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  + drop to add another row
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Box Spring Cover drop zone ────────────────────────────────────────────────

type BoxSpringTextField = Exclude<keyof BoxSpringRow, 'tempId' | 'draftQuoteObjectId'>;

const BOX_SPRING_FIELDS: [BoxSpringTextField, number, string][] = [
  ['roomWindow', 100, 'Room/Window'],
  ['quantity',   60,  'Quantity'],
  ['bedSize',    90,  'Bed Size'],
  ['fabric',     140, 'Fabric'],
  ['lwh',        140, 'Box Spring L/W/H'],
];

function BoxSpringDropZone({ rows, dragOverIndex, saving, onDragOver, onDragLeave, onDrop, onUpdate, onRemove }: {
  rows: BoxSpringRow[];
  dragOverIndex: number | null;
  saving: boolean;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number) => void;
  onUpdate: (i: number, field: BoxSpringTextField, val: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div
      style={{ ...s.dropZone, borderColor: dragOverIndex === -1 ? '#6366f1' : '#d1d5db', backgroundColor: dragOverIndex === -1 ? '#eef2ff' : '#fafafa' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(-1); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
    >
      {rows.length === 0 ? (
        <div style={s.emptyDrop}>Drop box spring cover objects here</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead>
              <tr>
                {BOX_SPRING_FIELDS.map(([, , label]) => <th key={label} style={s.th}>{label}</th>)}
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.tempId}
                  style={{ backgroundColor: dragOverIndex === i ? '#eef2ff' : undefined }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(i); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(i); }}
                >
                  {BOX_SPRING_FIELDS.map(([field, w]) => (
                    <td key={field} style={s.td}>
                      <input type="text" value={row[field]} onChange={(e) => onUpdate(i, field, e.target.value)} style={{ ...s.cellInput, minWidth: w, width: '100%' }} disabled={saving} />
                    </td>
                  ))}
                  <td style={s.td}>
                    <button onClick={() => onRemove(i)} style={s.removeBtn} disabled={saving} title="Remove"><CloseIcon sx={{ fontSize: 14 }} /></button>
                  </td>
                </tr>
              ))}
              <tr
                style={{ height: 28, backgroundColor: dragOverIndex === rows.length ? '#eef2ff' : undefined }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(rows.length); }}
                onDragLeave={onDragLeave}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(rows.length); }}
              >
                <td colSpan={BOX_SPRING_FIELDS.length + 1} style={{ ...s.td, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  + drop to add another row
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Duvet drop zone ──────────────────────────────────────────────────────────

type DuvetTextField = Exclude<keyof DuvetRow, 'tempId' | 'draftQuoteObjectId'>;

const DUVET_FIELDS: [DuvetTextField, number, string][] = [
  ['roomWindow', 100, 'Room/Window'],
  ['quantity',   60,  'Quantity'],
  ['bedSize',    90,  'Size'],
  ['fabric',     140, 'Fabric'],
  ['selfWelt',   80,  'Self Welt'],
];

function DuvetDropZone({ rows, dragOverIndex, saving, onDragOver, onDragLeave, onDrop, onUpdate, onRemove }: {
  rows: DuvetRow[];
  dragOverIndex: number | null;
  saving: boolean;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number) => void;
  onUpdate: (i: number, field: DuvetTextField, val: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div
      style={{ ...s.dropZone, borderColor: dragOverIndex === -1 ? '#6366f1' : '#d1d5db', backgroundColor: dragOverIndex === -1 ? '#eef2ff' : '#fafafa' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(-1); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
    >
      {rows.length === 0 ? (
        <div style={s.emptyDrop}>Drop duvet objects here</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead>
              <tr>
                {DUVET_FIELDS.map(([, , label]) => <th key={label} style={s.th}>{label}</th>)}
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.tempId}
                  style={{ backgroundColor: dragOverIndex === i ? '#eef2ff' : undefined }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(i); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(i); }}
                >
                  {DUVET_FIELDS.map(([field, w]) => (
                    <td key={field} style={s.td}>
                      <input type="text" value={row[field]} onChange={(e) => onUpdate(i, field, e.target.value)} style={{ ...s.cellInput, minWidth: w, width: '100%' }} disabled={saving} />
                    </td>
                  ))}
                  <td style={s.td}>
                    <button onClick={() => onRemove(i)} style={s.removeBtn} disabled={saving} title="Remove"><CloseIcon sx={{ fontSize: 14 }} /></button>
                  </td>
                </tr>
              ))}
              <tr
                style={{ height: 28, backgroundColor: dragOverIndex === rows.length ? '#eef2ff' : undefined }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(rows.length); }}
                onDragLeave={onDragLeave}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(rows.length); }}
              >
                <td colSpan={DUVET_FIELDS.length + 1} style={{ ...s.td, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  + drop to add another row
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Coverlet drop zone ───────────────────────────────────────────────────────

type CoverletTextField = Exclude<keyof CoverletRow, 'tempId' | 'draftQuoteObjectId'>;

const COVERLET_FIELDS: [CoverletTextField, number, string][] = [
  ['roomWindow', 100, 'Room/Window'],
  ['quantity',   60,  'Quantity'],
  ['bedSize',    90,  'Size'],
  ['fabric',     140, 'Fabric'],
];

function CoverletDropZone({ rows, dragOverIndex, saving, onDragOver, onDragLeave, onDrop, onUpdate, onRemove }: {
  rows: CoverletRow[];
  dragOverIndex: number | null;
  saving: boolean;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number) => void;
  onUpdate: (i: number, field: CoverletTextField, val: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div
      style={{ ...s.dropZone, borderColor: dragOverIndex === -1 ? '#6366f1' : '#d1d5db', backgroundColor: dragOverIndex === -1 ? '#eef2ff' : '#fafafa' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(-1); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
    >
      {rows.length === 0 ? (
        <div style={s.emptyDrop}>Drop coverlet objects here</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead>
              <tr>
                {COVERLET_FIELDS.map(([, , label]) => <th key={label} style={s.th}>{label}</th>)}
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.tempId}
                  style={{ backgroundColor: dragOverIndex === i ? '#eef2ff' : undefined }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(i); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(i); }}
                >
                  {COVERLET_FIELDS.map(([field, w]) => (
                    <td key={field} style={s.td}>
                      <input type="text" value={row[field]} onChange={(e) => onUpdate(i, field, e.target.value)} style={{ ...s.cellInput, minWidth: w, width: '100%' }} disabled={saving} />
                    </td>
                  ))}
                  <td style={s.td}>
                    <button onClick={() => onRemove(i)} style={s.removeBtn} disabled={saving} title="Remove"><CloseIcon sx={{ fontSize: 14 }} /></button>
                  </td>
                </tr>
              ))}
              <tr
                style={{ height: 28, backgroundColor: dragOverIndex === rows.length ? '#eef2ff' : undefined }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(rows.length); }}
                onDragLeave={onDragLeave}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(rows.length); }}
              >
                <td colSpan={COVERLET_FIELDS.length + 1} style={{ ...s.td, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  + drop to add another row
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Round Tablecloth drop zone ───────────────────────────────────────────────

type TableclothRoundTextField = Exclude<keyof TableclothRoundRow, 'tempId' | 'draftQuoteObjectId'>;

const TABLECLOTH_ROUND_FIELDS: [TableclothRoundTextField, number, string][] = [
  ['roomWindow', 100, 'Room/Window'],
  ['quantity',   60,  'Quantity'],
  ['size',       140, 'Size'],
  ['fabric',     140, 'Fabric'],
];

function TableclothRoundDropZone({ rows, dragOverIndex, saving, onDragOver, onDragLeave, onDrop, onUpdate, onRemove }: {
  rows: TableclothRoundRow[];
  dragOverIndex: number | null;
  saving: boolean;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number) => void;
  onUpdate: (i: number, field: TableclothRoundTextField, val: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div
      style={{ ...s.dropZone, borderColor: dragOverIndex === -1 ? '#6366f1' : '#d1d5db', backgroundColor: dragOverIndex === -1 ? '#eef2ff' : '#fafafa' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(-1); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
    >
      {rows.length === 0 ? (
        <div style={s.emptyDrop}>Drop round tablecloth objects here</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead>
              <tr>
                {TABLECLOTH_ROUND_FIELDS.map(([, , label]) => <th key={label} style={s.th}>{label}</th>)}
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.tempId}
                  style={{ backgroundColor: dragOverIndex === i ? '#eef2ff' : undefined }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(i); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(i); }}
                >
                  {TABLECLOTH_ROUND_FIELDS.map(([field, w]) => (
                    <td key={field} style={s.td}>
                      <input type="text" value={row[field]} onChange={(e) => onUpdate(i, field, e.target.value)} style={{ ...s.cellInput, minWidth: w, width: '100%' }} disabled={saving} />
                    </td>
                  ))}
                  <td style={s.td}>
                    <button onClick={() => onRemove(i)} style={s.removeBtn} disabled={saving} title="Remove"><CloseIcon sx={{ fontSize: 14 }} /></button>
                  </td>
                </tr>
              ))}
              <tr
                style={{ height: 28, backgroundColor: dragOverIndex === rows.length ? '#eef2ff' : undefined }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(rows.length); }}
                onDragLeave={onDragLeave}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(rows.length); }}
              >
                <td colSpan={TABLECLOTH_ROUND_FIELDS.length + 1} style={{ ...s.td, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  + drop to add another row
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Rectangle Tablecloth drop zone ───────────────────────────────────────────

type TableclothRectangleTextField = Exclude<keyof TableclothRectangleRow, 'tempId' | 'draftQuoteObjectId'>;

const TABLECLOTH_RECTANGLE_FIELDS: [TableclothRectangleTextField, number, string][] = [
  ['roomWindow', 100, 'Room/Window'],
  ['quantity',   60,  'Quantity'],
  ['size',       140, 'Size'],
  ['fabric',     140, 'Fabric'],
];

function TableclothRectangleDropZone({ rows, dragOverIndex, saving, onDragOver, onDragLeave, onDrop, onUpdate, onRemove }: {
  rows: TableclothRectangleRow[];
  dragOverIndex: number | null;
  saving: boolean;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number) => void;
  onUpdate: (i: number, field: TableclothRectangleTextField, val: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div
      style={{ ...s.dropZone, borderColor: dragOverIndex === -1 ? '#6366f1' : '#d1d5db', backgroundColor: dragOverIndex === -1 ? '#eef2ff' : '#fafafa' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(-1); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
    >
      {rows.length === 0 ? (
        <div style={s.emptyDrop}>Drop rectangle tablecloth objects here</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead>
              <tr>
                {TABLECLOTH_RECTANGLE_FIELDS.map(([, , label]) => <th key={label} style={s.th}>{label}</th>)}
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.tempId}
                  style={{ backgroundColor: dragOverIndex === i ? '#eef2ff' : undefined }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(i); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(i); }}
                >
                  {TABLECLOTH_RECTANGLE_FIELDS.map(([field, w]) => (
                    <td key={field} style={s.td}>
                      <input type="text" value={row[field]} onChange={(e) => onUpdate(i, field, e.target.value)} style={{ ...s.cellInput, minWidth: w, width: '100%' }} disabled={saving} />
                    </td>
                  ))}
                  <td style={s.td}>
                    <button onClick={() => onRemove(i)} style={s.removeBtn} disabled={saving} title="Remove"><CloseIcon sx={{ fontSize: 14 }} /></button>
                  </td>
                </tr>
              ))}
              <tr
                style={{ height: 28, backgroundColor: dragOverIndex === rows.length ? '#eef2ff' : undefined }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(rows.length); }}
                onDragLeave={onDragLeave}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(rows.length); }}
              >
                <td colSpan={TABLECLOTH_RECTANGLE_FIELDS.length + 1} style={{ ...s.td, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  + drop to add another row
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Furniture drop zone ────────────────────────────────────────────────────────

type FurnitureTextField = Exclude<keyof FurnitureRow, 'tempId' | 'draftQuoteObjectId'>;

const FURNITURE_FIELDS: [FurnitureTextField, number, string][] = [
  ['roomWindow', 100, 'Room/Piece'],
  ['quantity',   60,  'Quantity'],
  ['type',       120, 'Type'],
  ['size',       160, 'Size (W×D×H)'],
  ['seat',       120, 'Seat'],
  ['arms',       120, 'Arms'],
  ['back',       90,  'Back'],
  ['legs',       160, 'Legs'],
  ['cushions',   180, 'Cushions'],
  ['deck',       90,  'Deck'],
  ['fabric',     140, 'Fabric'],
];

function FurnitureDropZone({ rows, dragOverIndex, saving, onDragOver, onDragLeave, onDrop, onUpdate, onRemove }: {
  rows: FurnitureRow[];
  dragOverIndex: number | null;
  saving: boolean;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number) => void;
  onUpdate: (i: number, field: FurnitureTextField, val: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div
      style={{ ...s.dropZone, borderColor: dragOverIndex === -1 ? '#6366f1' : '#d1d5db', backgroundColor: dragOverIndex === -1 ? '#eef2ff' : '#fafafa' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(-1); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
    >
      {rows.length === 0 ? (
        <div style={s.emptyDrop}>Drop furniture objects here</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead>
              <tr>
                {FURNITURE_FIELDS.map(([, , label]) => <th key={label} style={s.th}>{label}</th>)}
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.tempId}
                  style={{ backgroundColor: dragOverIndex === i ? '#eef2ff' : undefined }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(i); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(i); }}
                >
                  {FURNITURE_FIELDS.map(([field, w]) => (
                    <td key={field} style={s.td}>
                      <input type="text" value={row[field]} onChange={(e) => onUpdate(i, field, e.target.value)} style={{ ...s.cellInput, minWidth: w, width: '100%' }} disabled={saving} />
                    </td>
                  ))}
                  <td style={s.td}>
                    <button onClick={() => onRemove(i)} style={s.removeBtn} disabled={saving} title="Remove"><CloseIcon sx={{ fontSize: 14 }} /></button>
                  </td>
                </tr>
              ))}
              <tr
                style={{ height: 28, backgroundColor: dragOverIndex === rows.length ? '#eef2ff' : undefined }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(rows.length); }}
                onDragLeave={onDragLeave}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(rows.length); }}
              >
                <td colSpan={FURNITURE_FIELDS.length + 1} style={{ ...s.td, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  + drop to add another row
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Bench drop zone ────────────────────────────────────────────────────────────

type BenchTextField = Exclude<keyof BenchRow, 'tempId' | 'draftQuoteObjectId'>;

const BENCH_FIELDS: [BenchTextField, number, string][] = [
  ['roomWindow', 100, 'Room/Piece'],
  ['quantity',   60,  'Quantity'],
  ['family',     130, 'Family'],
  ['shape',      90,  'Shape'],
  ['style',      160, 'Style'],
  ['size',       180, 'Size (W×D×H)'],
  ['storage',    70,  'Storage'],
  ['legs',       160, 'Legs'],
  ['trim',       100, 'Trim'],
  ['nailheads',  140, 'Nailheads'],
  ['fabric',     140, 'Fabric'],
];

function BenchDropZone({ rows, dragOverIndex, saving, onDragOver, onDragLeave, onDrop, onUpdate, onRemove }: {
  rows: BenchRow[];
  dragOverIndex: number | null;
  saving: boolean;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number) => void;
  onUpdate: (i: number, field: BenchTextField, val: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div
      style={{ ...s.dropZone, borderColor: dragOverIndex === -1 ? '#6366f1' : '#d1d5db', backgroundColor: dragOverIndex === -1 ? '#eef2ff' : '#fafafa' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(-1); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
    >
      {rows.length === 0 ? (
        <div style={s.emptyDrop}>Drop bench objects here</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead>
              <tr>
                {BENCH_FIELDS.map(([, , label]) => <th key={label} style={s.th}>{label}</th>)}
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.tempId}
                  style={{ backgroundColor: dragOverIndex === i ? '#eef2ff' : undefined }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(i); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(i); }}
                >
                  {BENCH_FIELDS.map(([field, w]) => (
                    <td key={field} style={s.td}>
                      <input type="text" value={row[field]} onChange={(e) => onUpdate(i, field, e.target.value)} style={{ ...s.cellInput, minWidth: w, width: '100%' }} disabled={saving} />
                    </td>
                  ))}
                  <td style={s.td}>
                    <button onClick={() => onRemove(i)} style={s.removeBtn} disabled={saving} title="Remove"><CloseIcon sx={{ fontSize: 14 }} /></button>
                  </td>
                </tr>
              ))}
              <tr
                style={{ height: 28, backgroundColor: dragOverIndex === rows.length ? '#eef2ff' : undefined }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(rows.length); }}
                onDragLeave={onDragLeave}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(rows.length); }}
              >
                <td colSpan={BENCH_FIELDS.length + 1} style={{ ...s.td, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  + drop to add another row
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Cornice drop zone ────────────────────────────────────────────────────────────

type CorniceTextField = Exclude<keyof CorniceRow, 'tempId' | 'draftQuoteObjectId'>;

const CORNICE_FIELDS: [CorniceTextField, number, string][] = [
  ['roomWindow', 100, 'Room/Piece'],
  ['quantity',   60,  'Quantity'],
  ['mount',      110, 'Mount'],
  ['width',      80,  'Width'],
  ['height',     80,  'Height'],
  ['returns',    130, 'Returns'],
  ['shape',      110, 'Shape'],
  ['trim',       140, 'Trim'],
  ['welt',       120, 'Self Welt'],
  ['nailheads',  140, 'Nailheads'],
  ['fabric',     140, 'Fabric'],
];

function CorniceDropZone({ rows, dragOverIndex, saving, onDragOver, onDragLeave, onDrop, onUpdate, onRemove }: {
  rows: CorniceRow[];
  dragOverIndex: number | null;
  saving: boolean;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number) => void;
  onUpdate: (i: number, field: CorniceTextField, val: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div
      style={{ ...s.dropZone, borderColor: dragOverIndex === -1 ? '#6366f1' : '#d1d5db', backgroundColor: dragOverIndex === -1 ? '#eef2ff' : '#fafafa' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(-1); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
    >
      {rows.length === 0 ? (
        <div style={s.emptyDrop}>Drop cornice objects here</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead>
              <tr>
                {CORNICE_FIELDS.map(([, , label]) => <th key={label} style={s.th}>{label}</th>)}
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.tempId}
                  style={{ backgroundColor: dragOverIndex === i ? '#eef2ff' : undefined }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(i); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(i); }}
                >
                  {CORNICE_FIELDS.map(([field, w]) => (
                    <td key={field} style={s.td}>
                      <input type="text" value={row[field]} onChange={(e) => onUpdate(i, field, e.target.value)} style={{ ...s.cellInput, minWidth: w, width: '100%' }} disabled={saving} />
                    </td>
                  ))}
                  <td style={s.td}>
                    <button onClick={() => onRemove(i)} style={s.removeBtn} disabled={saving} title="Remove"><CloseIcon sx={{ fontSize: 14 }} /></button>
                  </td>
                </tr>
              ))}
              <tr
                style={{ height: 28, backgroundColor: dragOverIndex === rows.length ? '#eef2ff' : undefined }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(rows.length); }}
                onDragLeave={onDragLeave}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(rows.length); }}
              >
                <td colSpan={CORNICE_FIELDS.length + 1} style={{ ...s.td, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  + drop to add another row
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Bed drop zone ──────────────────────────────────────────────────────────────

type BedTextField = Exclude<keyof BedRow, 'tempId' | 'draftQuoteObjectId'>;

const BED_FIELDS: [BedTextField, number, string][] = [
  ['roomWindow',               100, 'Room/Piece'],
  ['quantity',                 60,  'Quantity'],
  ['config',                   110, 'Config'],
  ['style',                    120, 'Style'],
  ['headboardSize',            150, 'Headboard Size'],
  ['edgeTreatment',            150, 'Edge Treatment'],
  ['mattressSize',             150, 'Mattress Size'],
  ['frameThickness',           90,  'Frame Thickness'],
  ['sideRailFootboardHeight',  110, 'SR/FB Height'],
  ['styleDescription',         140, 'SR/FB Style'],
  ['drawers',                  110, 'Drawers'],
  ['trundle',                  70,  'Trundle'],
  ['feet',                     140, 'Feet'],
  ['bunkyBoards',              120, 'Bunky Boards'],
  ['fabric',                   140, 'Fabric'],
];

function BedDropZone({ rows, dragOverIndex, saving, onDragOver, onDragLeave, onDrop, onUpdate, onRemove }: {
  rows: BedRow[];
  dragOverIndex: number | null;
  saving: boolean;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number) => void;
  onUpdate: (i: number, field: BedTextField, val: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div
      style={{ ...s.dropZone, borderColor: dragOverIndex === -1 ? '#6366f1' : '#d1d5db', backgroundColor: dragOverIndex === -1 ? '#eef2ff' : '#fafafa' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(-1); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
    >
      {rows.length === 0 ? (
        <div style={s.emptyDrop}>Drop bed objects here</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead>
              <tr>
                {BED_FIELDS.map(([, , label]) => <th key={label} style={s.th}>{label}</th>)}
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.tempId}
                  style={{ backgroundColor: dragOverIndex === i ? '#eef2ff' : undefined }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(i); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(i); }}
                >
                  {BED_FIELDS.map(([field, w]) => (
                    <td key={field} style={s.td}>
                      <input type="text" value={row[field]} onChange={(e) => onUpdate(i, field, e.target.value)} style={{ ...s.cellInput, minWidth: w, width: '100%' }} disabled={saving} />
                    </td>
                  ))}
                  <td style={s.td}>
                    <button onClick={() => onRemove(i)} style={s.removeBtn} disabled={saving} title="Remove"><CloseIcon sx={{ fontSize: 14 }} /></button>
                  </td>
                </tr>
              ))}
              <tr
                style={{ height: 28, backgroundColor: dragOverIndex === rows.length ? '#eef2ff' : undefined }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(rows.length); }}
                onDragLeave={onDragLeave}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(rows.length); }}
              >
                <td colSpan={BED_FIELDS.length + 1} style={{ ...s.td, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  + drop to add another row
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Roller shade drop zone ────────────────────────────────────────────────────

type RollerTextField = Exclude<keyof RollerRow, 'tempId' | 'draftQuoteObjectId'>;

const ROLLER_FIELDS: [RollerTextField, string][] = [
  ['roomWindow',    'ROOM/WINDOW'],
  ['windowWidth',   'WINDOW WIDTH'],
  ['windowLength',  'WINDOW LENGTH'],
  ['tubeSize',      'TUBE SIZE'],
  ['fabric',        'FABRIC'],
  ['drive',         'DRIVE'],
  ['motor',         'OPERATING SYSTEM'],
  ['rollDirection', 'ROLL DIRECTION'],
  ['rtOrLt',        'RT or LT'],
  ['insideOutside', 'Inside/Outside'],
];

function RollerDropZone({ rows, dragOverIndex, saving, onDragOver, onDragLeave, onDrop, onUpdate, onRemove, onAddEmpty }: {
  rows: RollerRow[];
  dragOverIndex: number | null;
  saving: boolean;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (idx: number) => void;
  onUpdate: (i: number, field: RollerTextField, val: string) => void;
  onRemove: (i: number) => void;
  onAddEmpty: () => void;
}) {
  return (
    <div
      style={{ ...s.dropZone, minHeight: 140, borderColor: dragOverIndex === -1 ? '#6366f1' : '#d1d5db', backgroundColor: dragOverIndex === -1 ? '#eef2ff' : '#fafafa' }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(-1); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
    >
      {rows.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 120, gap: 12 }}>
          <div style={s.emptyDrop}>Drop objects here to add shades</div>
          <button onClick={onAddEmpty} style={{ ...s.saveBtn, fontSize: 12, padding: '6px 14px' }}>+ Add Shade Manually</button>
        </div>
      ) : (
        <div style={{ padding: 12, display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          {rows.map((row, i) => (
            <div
              key={row.tempId}
              style={{ border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: '#fff', padding: 12, minWidth: 260, flex: '1 1 260px', maxWidth: 320 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: '#374151' }}>{i + 1}</span>
                <button onClick={() => onRemove(i)} style={s.removeBtn} disabled={saving} title="Remove"><CloseIcon sx={{ fontSize: 14 }} /></button>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {ROLLER_FIELDS.map(([field, label]) => (
                    <tr key={field}>
                      <td style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', paddingRight: 10, paddingBottom: 6, whiteSpace: 'nowrap', textAlign: 'right', width: '45%' }}>
                        {label}
                      </td>
                      <td style={{ paddingBottom: 6 }}>
                        <input
                          type="text"
                          value={row[field]}
                          onChange={(e) => onUpdate(i, field, e.target.value)}
                          disabled={saving}
                          style={{ width: '100%', fontSize: 13, border: 'none', borderBottom: '1px solid #e5e7eb', outline: 'none', padding: '1px 4px', fontFamily: 'inherit', backgroundColor: 'transparent' }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <div
            style={{ minWidth: 180, flex: '1 1 180px', maxWidth: 220, border: '2px dashed #d1d5db', borderRadius: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 16, backgroundColor: dragOverIndex === rows.length ? '#eef2ff' : 'transparent' }}
            onDragOver={(e) => { e.preventDefault(); onDragOver(rows.length); }}
            onDragLeave={onDragLeave}
            onDrop={(e) => { e.preventDefault(); onDrop(rows.length); }}
          >
            <span style={{ color: '#9ca3af', fontSize: 12 }}>Drop here</span>
            <button onClick={onAddEmpty} style={{ fontSize: 12, padding: '5px 12px', border: '1px solid #d1d5db', borderRadius: 5, background: '#fff', cursor: 'pointer', color: '#374151', fontWeight: 600 }} disabled={saving}>
              + Add Shade
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  overlay:       { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:         { backgroundColor: '#fff', borderRadius: 8, boxShadow: '0 24px 40px rgba(0,0,0,0.18)', width: '95vw', maxWidth: 1520, minHeight: '75vh', maxHeight: '94vh', display: 'flex', flexDirection: 'column' },
  modalHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 24px', borderBottom: '1px solid #e5e7eb' },
  modalTitle:    { margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' },
  closeBtn:      { background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', lineHeight: 1 },
  body:          { flex: 1, overflowY: 'auto', padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 16 },
  travelerHeader:{ display: 'flex', border: '1px solid #374151', borderRadius: 4, overflow: 'hidden' },
  hCell:         { flex: 1, display: 'flex', flexDirection: 'column', gap: 3, padding: '8px 12px', borderRight: '1px solid #374151' },
  hLabel:        { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' },
  hValue:        { fontSize: 15, fontWeight: 600, color: '#111827' },
  hInput:        { fontSize: 14, border: 'none', outline: 'none', padding: '2px 0', backgroundColor: 'transparent', color: '#111827', width: '100%' },
  label:         { display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 4 },
  input:         { width: '100%', padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' },
  panels:        { display: 'flex', gap: 16, alignItems: 'flex-start', flex: 1 },
  leftPanel:     { width: 250, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 480, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px' },
  rightPanel:    { flex: 1, display: 'flex', flexDirection: 'column', gap: 8 },
  panelTitle:    { fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', marginBottom: 2 },
  draggableItem: { display: 'flex', alignItems: 'flex-start', gap: 5, padding: '6px 8px', borderRadius: 5, cursor: 'grab', marginBottom: 3, userSelect: 'none' },
  dragHandle:    { fontSize: 14, color: '#9ca3af', flexShrink: 0 },
  itemDesc:      { fontSize: 13, fontWeight: 500, color: '#111827', wordBreak: 'break-word' },
  itemMeta:      { fontSize: 12, color: '#6b7280' },
  hint:          { fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: '3px 0' },
  dropZone:      { border: '2px dashed #d1d5db', borderRadius: 6, minHeight: 140, transition: 'border-color 0.15s, background-color 0.15s' },
  emptyDrop:     { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 130, color: '#9ca3af', fontSize: 14 },
  th:            { padding: '6px 5px', border: '1px solid #374151', backgroundColor: '#f9fafb', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap', textAlign: 'center', fontSize: 12 },
  td:            { padding: '4px 4px', border: '1px solid #d1d5db', verticalAlign: 'middle' },
  cellInput:     { border: 'none', outline: 'none', fontSize: 13, padding: '2px 4px', backgroundColor: 'transparent', fontFamily: 'inherit' },
  removeBtn:     { background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13, padding: '0 4px', lineHeight: 1 },
  textarea:      { width: '100%', padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', resize: 'vertical', minHeight: 80, boxSizing: 'border-box' },
  errorBox:      { padding: '10px 14px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: 6, fontSize: 13 },
  footer:        { display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '14px 24px', borderTop: '1px solid #e5e7eb' },
  cancelBtn:     { padding: '9px 22px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  saveBtn:       { padding: '9px 22px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};
